// In-app Dev Console v2 — manage dev cycles from the browser (internal only).
//
// Chat with a Cursor cloud agent that works on the app repo: it clones,
// edits, and opens a PR on a Cursor-hosted VM (never on prod infra). The
// workspace is tabbed — Chat, Activity (full event timeline), Terminal (agent
// shell + deploy output), PR & Deploy, Details (usage/cost/raw doc) — with
// live status: elapsed timer, last-activity pulse, per-session cost.
// Session management: stop, retry (re-run a failed/finished prompt), rename,
// delete. Sidebar has search + status filters.
//
// Access is triple-gated on the `internal: true` custom claim: nav visibility
// (shells), Firestore rules (read-only, internal only), and the backend API.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { db } from '../firebaseConfig';
import { onAuthChanged, getClaims, getIdToken } from '../data/authPlane';
import { isPg, subscribeRows, subscribeRow } from '../data/supabaseClient';
import { resolvePath } from '../data/pathMap';
import { isUploadConfigured } from '../data/uploadService';
import {
  collectFromFileList, collectFromDataTransfer, collectFromPaste, pickFolderEntries, applySkips, checkLimits, uploadAttachments,
} from '../dev/attachments';
import AttachmentTray, { QuestionsCard } from '../dev/AttachmentTray';
import { buildEnvelope, envelopeBlock, takeReport } from '../dev/devContext';
import {
  ModeSelector, ModelSelector, ModelParamsSelector, AdvancedAgentOptions,
  defaultParamsFor, buildParamsArray, agentOptionsPayload, DEFAULT_AGENT_OPTIONS,
  loadStoredConfig, saveStoredConfig, restoreModelPick, FALLBACK_MODES,
} from '../dev/AgentConfig';
import './DevConsole.css';
import { kitEnv } from '../data/kitEnv.js';
import EcosystemFrame, { EcosystemTicker } from '../components/three/EcosystemFrame';
import { deriveEcosystem } from '../components/three/ecosystemState';
import { deriveCommand } from '../components/three/commandState';
import CommandView from '../components/command/CommandView';
import useCommandToggle, { CommandToggle } from '../components/command/useCommandToggle';
import { appPanels } from '../components/command/appHud';
import useTelemetry, { TelemetryControls, ActivityStrip } from '../components/command/useTelemetry';
import { trafficPanel, anomalyPanel, AnomalyTable, FlowTable, RuntimeRows } from '../components/command/telemetryHud';

const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '');

// ---- auth-claims hook (exported for the shells' nav gating) -----------------
// `internal: true` gates the internal surfaces (Dev Console, Cost Report).
// `appRole` ('admin' | 'product' | 'tester' | 'waitlist') manages pre-launch
// visibility: 'waitlist' hides pre-launch surfaces. No role = tester
// behavior, since every existing account is an invited tester.
export function useClaims() {
  const [claims, setClaims] = useState({ internal: false, role: null });
  useEffect(() => {
    let alive = true;
    // Dual-plane: getClaims() normalizes Firebase ID-token claims and Supabase
    // app_metadata to the same { internal, appRole, … } shape; onAuthChanged
    // fires immediately with the current user on both planes.
    const check = async () => {
      try {
        const c = await getClaims();
        if (alive) setClaims({ internal: c.internal === true, role: c.appRole || null });
      } catch {
        if (alive) setClaims({ internal: false, role: null });
      }
    };
    const unsub = onAuthChanged(() => check());
    return () => { alive = false; unsub(); };
  }, []);
  return claims;
}

export function useInternalClaim() {
  return useClaims().internal;
}

async function api(path, body) {
  // Firebase ID token | Supabase access_token — the backend accepts both.
  const token = await getIdToken();
  const res = await fetch(`${API}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `${path}: ${res.status}`);
  return json;
}

// ---- mermaid (lazy) ----------------------------------------------------------
let mermaidReady = null;
async function getMermaid() {
  if (!mermaidReady) {
    mermaidReady = import('mermaid').then((m) => {
      const mermaid = m.default;
      mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', theme: 'dark' });
      return mermaid;
    });
  }
  return mermaidReady;
}

let mermaidSeq = 0;
function Mermaid({ code }) {
  const [svg, setSvg] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    getMermaid()
      .then((mermaid) => mermaid.render(`devmmd-${(mermaidSeq += 1)}`, code))
      .then(({ svg: out }) => { if (alive) setSvg(out); })
      .catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [code]);
  if (err) return <pre className="devc-mono">{code}</pre>;
  if (!svg) return <div className="devc-dim">Rendering diagram…</div>;
  // Mermaid output with securityLevel 'strict' is sanitized SVG.
  // eslint-disable-next-line react/no-danger
  return <div className="devc-mermaid" dangerouslySetInnerHTML={{ __html: svg }} />;
}

// Markdown with ```mermaid fences rendered as live diagrams.
const mdComponents = {
  code({ inline, className, children, ...props }) {
    const text = String(children).replace(/\n$/, '');
    if (!inline && /language-mermaid/.test(className || '')) return <Mermaid code={text} />;
    return <code className={className} {...props}>{children}</code>;
  },
};

// ---- status chip + helpers -----------------------------------------------------
const CHIP = {
  starting: ['Starting', 'blue'], running: ['Running', 'blue'], cancelling: ['Cancelling', 'amber'],
  finished: ['Finished', 'green'], error: ['Error', 'red'], 'startup-failed': ['Failed to start', 'red'],
  cancelled: ['Cancelled', 'gray'],
};
const Chip = ({ status }) => {
  const [label, tone] = CHIP[status] || [status || '—', 'gray'];
  return <span className={`devc-chip ${tone}`}>{label}</span>;
};

// What the run is doing right now, from the session's `phase` (written by
// the sovereign runner and the launch path) — the header and the working row
// say this instead of only how long it has been.
const PHASE_LABEL = {
  launch: 'Launching runner',
  mode: 'Choosing how to work',
  plan: 'Planning (no edits)',
  runner: 'Runner started',
  clone: 'Cloning repo',
  branch: 'Preparing branch',
  kit: 'Checking out kit reference',
  agent: 'Agent working',
  'agent-done': 'Agent finished, saving work',
  push: 'Committing and pushing',
  pr: 'Opening pull request',
  done: 'Done',
  failed: 'Failed',
};
// After this long without any event or heartbeat the UI says so plainly —
// the backend marks the session failed after dev.runnerStaleMinutes.
const STALL_MS = 90 * 1000;

function phaseLine(session) {
  if (!session) return '';
  const base = PHASE_LABEL[session.phase] || (session.status === 'starting' ? 'Waiting for the runner' : 'Working');
  if (session.phase === 'agent') {
    const n = session.toolCalls || 0;
    const tools = n ? `${n} tool call${n === 1 ? '' : 's'}` : 'reading the repo';
    return `${base} · ${tools}${session.lastTool ? ` · ${session.lastTool}` : ''}`;
  }
  return base;
}

// The one argument worth showing for a tool call: the path, command or
// query — whichever the tool took.
function toolArg(t) {
  let arg = t.args;
  try { const j = typeof arg === 'string' ? JSON.parse(arg) : arg; arg = j?.path || j?.file || j?.filePath || j?.target_file || j?.command || j?.cmd || j?.pattern || j?.query || j?.url || ''; } catch { /* raw */ }
  return arg ? String(arg) : '';
}

// Consecutive tool events fold into one compact row in the chat (the
// Activity tab keeps the full detail): "7 tool calls — shell ×2, edit ×3…".
function ToolGroup({ items }) {
  const [open, setOpen] = useState(false);
  const counts = {};
  for (const t of items) counts[t.name || 'tool'] = (counts[t.name || 'tool'] || 0) + 1;
  const summary = Object.entries(counts).map(([k, v]) => (v > 1 ? `${k} ×${v}` : k)).join(', ');
  const running = items.some((t) => t.status === 'running');
  return (
    <div className={`devc-toolgroup${running ? ' is-running' : ''}`}>
      <button type="button" className="devc-toolgroup-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className={`devc-step-dot${running ? ' live' : ''}`} aria-hidden="true" />
        {items.length} tool call{items.length === 1 ? '' : 's'} — {summary}
        <span className="devc-toolgroup-chev" aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <ol className="devc-toolgroup-list">
          {items.map((t) => {
            const arg = toolArg(t);
            return (
              <li key={t.id}>
                <span className="devc-toolgroup-name">{t.name}</span>
                {arg ? <code>{String(arg).slice(0, 140)}</code> : null}
                {t.status === 'running' && <span className="devc-chip blue">running</span>}
                {t.status === 'error' && <span className="devc-chip red">error</span>}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

const LIVE = ['starting', 'running', 'cancelling'];
const FAILED = ['error', 'startup-failed', 'cancelled'];

const fmtDur = (ms) => {
  if (ms == null || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};
const fmtAgo = (ts) => {
  if (!ts) return '';
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(ts).toLocaleDateString();
};
const fmtTok = (n) => (n == null ? '—' : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}k` : String(n));
const costUsd = (u) => {
  if (!u) return null;
  const cents = u.chargedCents ?? u.rawCostCents;
  return cents == null ? null : cents / 100;
};

// Re-render on a 1s tick while `on` (elapsed timers, "Xs ago" labels).
function useTick(on) {
  const [, setN] = useState(0);
  useEffect(() => {
    if (!on) return undefined;
    const t = setInterval(() => setN((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [on]);
}

// ---- tiny live-doc/collection hooks (compat SDK) --------------------------------
function useDoc(path) {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    if (!path) { setDoc(null); return undefined; }
    if (isPg()) {
      const { table, id } = resolvePath(path);
      return subscribeRow(table, id, setDoc, () => setDoc(null));
    }
    return db.doc(path).onSnapshot(
      (s) => setDoc(s.exists ? { id: s.id, ...s.data() } : null),
      () => setDoc(null)
    );
  }, [path]);
  return doc;
}

function useOrdered(path, field, dir = 'asc', limit = 500) {
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    if (!path) { setDocs([]); return undefined; }
    if (isPg()) {
      // devSessions / devSessions/{id}/events / devJobs/{id}/logs ride
      // postgres_changes on their doc-tables; order (e.g. data->>'seq') and
      // limit are applied client-side by subscribeRows.
      const { table, filters } = resolvePath(path);
      return subscribeRows(table, filters, { orderBy: field, dir, limit }, setDocs, () => setDocs([]));
    }
    return db.collection(path).orderBy(field, dir).limit(limit).onSnapshot(
      (s) => setDocs(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => setDocs([])
    );
  }, [path, field, dir, limit]);
  return docs;
}

// ---- deploy card ---------------------------------------------------------------
// Explicit push actions instead of a target picker: Push client (SPA build +
// publish), Push server (backend fast overlay, optional full image rebuild),
// Push rules (firebase plane only — on supabase, schema rides the server
// push). Each pushes to the selected environment; UAT-first, production is
// the deliberate promote. On stamped instances UAT is a second full instance
// sold as an add-on — no add-on renders the switch disabled with an upsell.
function DeployCard({ session, sessionId, setup }) {
  const uat = setup?.uat || { mode: 'service', configured: true };
  const uatReady = uat.mode === 'service' || !!uat.configured;
  const [env, setEnv] = useState(uatReady ? 'uat' : 'prod');
  const [fullRebuild, setFullRebuild] = useState(false);
  const [busy, setBusy] = useState(null); // which action is in flight
  const [err, setErr] = useState(null);
  const job = useDoc(session?.deployJobId ? `devJobs/${session.deployJobId}` : null);
  const showRules = !isPg();
  // Full rebuild on ECS UAT would leak into prod (shared image) — instance
  // UAT builds its own image, so the option stays live there.
  const fullAllowed = env !== 'uat' || uat.mode === 'instance';

  const push = async (label, targets, envOverride) => {
    setBusy(label); setErr(null);
    try {
      await api('/dev/deploy', { sessionId, targets, env: envOverride || env });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(null);
    }
  };

  if (!session?.prUrl) {
    return (
      <div className="devc-empty">
        <h3>No pull request yet</h3>
        <p>
          When the agent finishes a change it opens a PR against the repo — it will show up here with
          one-click Merge &amp; Deploy. {LIVE.includes(session?.status) ? 'The agent is still working…' : ''}
        </p>
      </div>
    );
  }
  const running = busy || job?.status === 'running' || job?.status === 'queued';
  const verb = session.prMerged ? 'Push' : 'Merge & push';
  return (
    <div className="devc-card">
      <div className="devc-card-title">
        Pull request
        {session.prMerged && <span className="devc-chip green">Merged</span>}
        {job && <span className={`devc-chip ${job.status === 'done' ? 'green' : job.status === 'error' ? 'red' : 'blue'}`}>{job.env === 'uat' ? 'UAT' : 'Prod'} deploy: {job.stage || job.status}</span>}
      </div>
      <a href={session.prUrl} target="_blank" rel="noopener noreferrer" className="devc-link">{session.prUrl}</a>
      {session.branch && <div className="devc-dim">branch {session.branch}</div>}
      <div className="devc-env-pick">
        <label className={`${env === 'uat' ? 'active' : ''} ${uatReady ? '' : 'devc-dim'}`}>
          <input
            type="radio"
            name="devc-env"
            disabled={!uatReady}
            checked={env === 'uat'}
            onChange={() => setEnv('uat')}
          />
          <strong>UAT</strong> — {uat.mode === 'instance' ? 'your staging instance' : 'staging tier'} (default landing zone)
        </label>
        <label className={env === 'prod' ? 'active devc-env-prod' : ''}>
          <input type="radio" name="devc-env" checked={env === 'prod'} onChange={() => setEnv('prod')} />
          <strong>Production</strong> — live app (promote after UAT checks)
        </label>
      </div>
      {uat.mode === 'instance' && !uat.configured && (
        <div className="devc-upsell">
          <strong>Add a UAT environment</strong> — a full second instance of your app
          (<code>-uat</code> URL, own database) to test changes on before they touch
          production.{' '}
          <a href="https://nventr.io/orgs" target="_blank" rel="noopener noreferrer">
            Add UAT from your org page →
          </a>
        </div>
      )}
      <div className="devc-push-row">
        <button
          className={`devc-btn ${env === 'prod' ? 'danger' : 'primary'}`}
          disabled={running}
          onClick={() => push('client', ['frontend'])}
          title="Build the SPA from merged main and publish it"
        >
          {busy === 'client' ? 'Pushing…' : `${verb} client → ${env === 'prod' ? 'Prod' : 'UAT'}`}
        </button>
        <button
          className={`devc-btn ${env === 'prod' ? 'danger' : 'primary'}`}
          disabled={running || (fullRebuild && !fullAllowed)}
          onClick={() => push('server', [fullRebuild && fullAllowed ? 'backend-full' : 'backend-fast'])}
          title="Deploy the backend from merged main (fast overlay unless full rebuild is checked)"
        >
          {busy === 'server' ? 'Pushing…' : `${verb} server → ${env === 'prod' ? 'Prod' : 'UAT'}`}
        </button>
        {showRules && (
          <button
            className="devc-btn"
            disabled={running}
            onClick={() => push('rules', ['rules'])}
            title="Publish Firestore security rules"
          >
            {busy === 'rules' ? 'Pushing…' : 'Push rules'}
          </button>
        )}
      </div>
      <label className={`devc-full-opt ${fullAllowed ? '' : 'devc-dim'}`}>
        <input
          type="checkbox"
          checked={fullRebuild && fullAllowed}
          disabled={!fullAllowed}
          onChange={(e) => setFullRebuild(e.target.checked)}
          title={fullAllowed ? undefined : 'Production-only on this tier: the container image is shared'}
        />
        Full server rebuild (deps / Dockerfile changes — slower)
      </label>
      {uatReady && env === 'uat' && (
        <button
          className="devc-btn devc-promote"
          disabled={running}
          onClick={() => push('promote', ['frontend', 'backend-fast', ...(showRules ? ['rules'] : [])], 'prod')}
          title="Deploy the same merged main to production (client + server)"
        >
          {busy === 'promote' ? 'Promoting…' : '⬆ Promote UAT → production'}
        </button>
      )}
      {job?.status === 'error' && <div className="devc-err">Deploy failed: {job.message}</div>}
      {err && <div className="devc-err">{err}</div>}
      {job && <JobLogTail jobId={session.deployJobId} />}
      {job?.forwarded && (
        <div className="devc-dim">
          Running on the UAT instance — full logs live in its own Dev Console.
        </div>
      )}
    </div>
  );
}

// Preview channel: build the PR branch onto a temporary Firebase Hosting URL —
// see the change live before merging anything.
function PreviewCard({ session, sessionId, onReady }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const job = useDoc(session?.previewJobId ? `devJobs/${session.previewJobId}` : null);
  const building = job && ['queued', 'running'].includes(job.status);

  const go = async () => {
    setBusy(true); setErr(null);
    try {
      await api('/dev/preview', { sessionId });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!session?.prUrl || session.prMerged) return null;
  return (
    <div className="devc-card">
      <div className="devc-card-title">
        Preview before you ship
        {job && <span className={`devc-chip ${job.status === 'done' ? 'green' : job.status === 'error' ? 'red' : 'blue'}`}>Preview: {job.stage || job.status}</span>}
      </div>
      <p className="devc-dim" style={{ margin: '2px 0 10px' }}>
        Builds this PR&apos;s branch to a temporary URL (expires in 7 days). Production stays untouched —
        merge only when it looks right.
      </p>
      {session.previewUrl && (
        <div style={{ marginBottom: 10 }}>
          <a className="devc-link" href={session.previewUrl} target="_blank" rel="noopener noreferrer">{session.previewUrl}</a>
          {onReady && <button className="devc-btn" style={{ marginLeft: 10 }} onClick={onReady}>Open Preview tab</button>}
        </div>
      )}
      <button className="devc-btn primary" disabled={busy || building} onClick={go}>
        {building ? 'Building preview…' : session.previewUrl ? 'Rebuild preview' : 'Build preview'}
      </button>
      {job?.status === 'error' && <div className="devc-err">Preview failed: {job.message}</div>}
      {err && <div className="devc-err">{err}</div>}
      {building && <JobLogTail jobId={session.previewJobId} />}
    </div>
  );
}

function JobLogTail({ jobId }) {
  const logs = useOrdered(jobId ? `devJobs/${jobId}/logs` : null, 'seq', 'asc', 1000);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: 'nearest' }); }, [logs.length]);
  if (!logs.length) return null;
  return (
    <div className="devc-joblog">
      {logs.slice(-40).map((l) => <pre key={l.id}>{l.text}</pre>)}
      <div ref={end} />
    </div>
  );
}

// ---- plan card -------------------------------------------------------------------
// A plan-mode run's final plan arrives as a dedicated `plan` event (full
// markdown — backend extracts it from the SDK's createPlan tool call). It
// renders as a structured card in the chat timeline, visually distinct from
// regular messages, so the user reviews exactly what will run BEFORE
// Approve & Execute. Long plans start collapsed; a regenerated plan
// supersedes earlier ones, which collapse to a stub instead of leaving the
// history ambiguous about which plan will execute.
const PLAN_COLLAPSE_CHARS = 2500;

// Does a result event's text just repeat the assistant text streamed since
// the previous user turn? (Both runtimes' CLIs/SDKs hand back the final
// message as the "result" too.)
function resultRepeatsAssistant(result, events) {
  const norm = (t) => String(t || '').replace(/\s+/g, ' ').trim();
  const r = norm(result.text);
  if (!r) return false;
  const parts = [];
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const e = events[i];
    if (e.seq >= result.seq) continue;
    if (e.type === 'user') break;
    if (e.type === 'assistant' && e.text) parts.unshift(norm(e.text));
  }
  if (!parts.length) return false;
  if (parts[parts.length - 1] === r) return true;
  const joined = norm(parts.join(' '));
  return joined === r || (r.length > 40 && joined.includes(r));
}

function PlanCard({ e, superseded, pending, execLabel, execInvalid }) {
  const [expanded, setExpanded] = useState(false);
  // A plan under review is never folded — members read "Phase 1" above the
  // fade and thought that was the whole plan. Only executed/old plans fold.
  const long = !pending && (e.text || '').length > PLAN_COLLAPSE_CHARS;
  if (superseded) {
    return (
      <div className="devc-msg plan superseded">
        <div className="devc-msg-who">
          📋 Plan <span className="devc-chip gray">superseded</span>
        </div>
        {expanded
          ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{e.text || '(empty plan)'}</ReactMarkdown>
          : null}
        <button className="devc-filter" onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Hide old plan' : 'Show old plan'}
        </button>
      </div>
    );
  }
  return (
    <div className="devc-msg plan">
      <div className="devc-msg-who">
        📋 Execution plan{pending ? ' — review before approving' : ''}
      </div>
      {e.error && <div className="devc-err">Plan generation failed: {e.error}</div>}
      {!e.error && !e.text && pending && (
        <div className="devc-err">
          The planning run finished but the plan came back empty — use &ldquo;Revise plan…&rdquo; to ask
          the agent to regenerate it. Approval stays blocked until a plan renders here.
        </div>
      )}
      {e.text && (
        <div className={`devc-plan-body${long && !expanded ? ' collapsed' : ''}`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{e.text}</ReactMarkdown>
        </div>
      )}
      {e.text && long && (
        <button className="devc-filter" onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Collapse plan' : `Show full plan (${Math.round(e.text.length / 1000)}k chars)`}
        </button>
      )}
      {pending && execLabel && (
        <div className="devc-plan-model">
          Executes with <strong>{execLabel}</strong>
          {execInvalid
            ? <span className="devc-chip red">not in the current model catalog</span>
            : <span className="devc-dim">— change it in the approval bar below</span>}
        </div>
      )}
    </div>
  );
}

// Resolve a model string against the live catalog the way the backend does:
// canonical id, any alias, or display name (case/whitespace-insensitive).
// Returns the catalog entry or null when the catalog positively doesn't know
// the model.
const normModelKey = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
function findCatalogModel(models, value) {
  const want = normModelKey(value);
  if (!want) return null;
  return (models || []).find((m) => normModelKey(m.id) === want
    || (m.aliases || []).some((a) => normModelKey(a) === want)
    || normModelKey(m.name) === want) || null;
}

// ---- activity timeline -----------------------------------------------------------
const EVENT_META = {
  user: ['💬', 'You'],
  assistant: ['🤖', 'Agent'],
  thinking: ['🧠', 'Thinking'],
  plan: ['📋', 'Plan'],
  questions: ['❓', 'Questions'],
  tool: ['⚙️', 'Tool'],
  status: ['ℹ️', 'Status'],
  usage: ['🎟️', 'Tokens'],
  result: ['🏁', 'Result'],
};

function ActivityRow({ e }) {
  const [icon, label] = EVENT_META[e.type] || ['•', e.type];
  let body = null;
  if (e.type === 'tool') {
    body = (
      <>
        <span className="devc-act-name">{e.name}</span>
        <span className={`devc-chip ${e.status === 'running' ? 'blue' : e.status === 'error' ? 'red' : 'gray'}`}>{e.status}</span>
        {e.args && <code className="devc-act-args">{e.args}</code>}
        {e.result && <pre className="devc-act-result">{e.result}</pre>}
      </>
    );
  } else if (e.type === 'usage') {
    body = <span className="devc-dim">{fmtTok(e.inputTokens)} in · {fmtTok(e.outputTokens)} out this turn</span>;
  } else if (e.type === 'questions') {
    body = <span className="devc-act-text">{(e.questions || []).map((q) => q.question).join(' · ').slice(0, 400)}</span>;
  } else if (e.type === 'result') {
    body = (
      <>
        <Chip status={e.status} />
        {e.error && <div className="devc-err">{e.error}</div>}
        {e.prUrl && <a className="devc-link" href={e.prUrl} target="_blank" rel="noopener noreferrer">{e.prUrl}</a>}
      </>
    );
  } else {
    body = <span className="devc-act-text">{(e.text || e.message || '').slice(0, 400)}</span>;
  }
  return (
    <div className={`devc-act ${e.type}`}>
      <div className="devc-act-icon">{icon}</div>
      <div className="devc-act-body">
        <div className="devc-act-head">
          <strong>{label}</strong>
          <span className="devc-dim">{e.ts ? new Date(e.ts).toLocaleTimeString() : ''}</span>
        </div>
        {body}
      </div>
    </div>
  );
}

// ---- inline PR diff --------------------------------------------------------------
// Per-file patches from GET /dev/sessions/:id/diff — review the PR without a
// GitHub round trip. First files start expanded.
function parsePatch(patch) {
  const rows = [];
  let oldN = 0;
  let newN = 0;
  for (const line of String(patch || '').replace(/\n$/, '').split('\n')) {
    const hunk = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/.exec(line);
    if (hunk) {
      oldN = Number(hunk[1]);
      newN = Number(hunk[2]);
      rows.push({ kind: 'hunk', text: line });
    } else if (line.startsWith('diff ') || line.startsWith('index ') || line.startsWith('---') || line.startsWith('+++') || line.startsWith('\\')) {
      rows.push({ kind: 'meta', text: line });
    } else if (line.startsWith('+')) {
      rows.push({ kind: 'add', old: '', neu: String(newN), text: line.slice(1) });
      newN += 1;
    } else if (line.startsWith('-')) {
      rows.push({ kind: 'del', old: String(oldN), neu: '', text: line.slice(1) });
      oldN += 1;
    } else {
      const text = line.startsWith(' ') ? line.slice(1) : line;
      rows.push({ kind: 'ctx', old: String(oldN), neu: String(newN), text });
      oldN += 1;
      newN += 1;
    }
  }
  return rows;
}

function DiffViewer({ sessionId, prUrl }) {
  const [diff, setDiff] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState({});
  useEffect(() => {
    if (!prUrl) return undefined;
    let alive = true;
    api(`/dev/sessions/${sessionId}/diff`)
      .then((d) => {
        if (!alive) return;
        setDiff(d);
        const first = {};
        (d.files || []).slice(0, 3).forEach((f) => { first[f.filename] = true; });
        setOpen(first);
      })
      .catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [sessionId, prUrl]);

  if (!prUrl) return null;
  if (err) return <div className="devc-err">Diff unavailable: {err}</div>;
  if (!diff) return <div className="devc-dim" style={{ padding: '8px 2px' }}>Loading diff…</div>;
  return (
    <div className="devc-card">
      <div className="devc-card-title">
        Changes in this PR
        <span className="devc-chip gray">{diff.files.length} file{diff.files.length === 1 ? '' : 's'}</span>
        <span className="devc-chip green">+{diff.files.reduce((n, f) => n + (f.additions || 0), 0)}</span>
        <span className="devc-chip red">−{diff.files.reduce((n, f) => n + (f.deletions || 0), 0)}</span>
      </div>
      {diff.files.map((f) => (
        <div key={f.filename} className="devc-diff-file">
          <button className="devc-diff-head" onClick={() => setOpen({ ...open, [f.filename]: !open[f.filename] })}>
            <span className="devc-diff-name">{open[f.filename] ? '▾' : '▸'} {f.filename}</span>
            <span className="devc-dim">{f.status} · +{f.additions} −{f.deletions}</span>
          </button>
          {open[f.filename] && (
            <pre className="devc-diff-body">
              {(f.patch ? parsePatch(f.patch) : [{ kind: 'meta', text: '(no textual diff — binary or too large)' }]).map((row, i) => (
                <span
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  className={row.kind}
                >
                  {row.kind === 'hunk' || row.kind === 'meta' ? row.text : (
                    <>
                      <i>{row.old}</i>
                      <i>{row.neu}</i>
                      <b>{row.kind === 'add' ? '+' : row.kind === 'del' ? '−' : ' '}</b>
                      {row.text}
                    </>
                  )}
                </span>
              ))}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
}

// ---- attachments ----------------------------------------------------------------
// Any file type, any count within live-config limits (dev.attachments via
// /dev/status): screenshots and documents stay first-class, whole folders
// become a data package (frontend/src/dev/attachments.js). The hint below
// is only what the picker suggests; nothing is rejected by extension.
const DOC_EXT_LIST = '.txt,.md,.markdown,.csv,.tsv,.json,.log,.yaml,.yml,.xml,.html,.pdf,.docx';

// ---- structured prompt templates ------------------------------------------------
// Each chip collects named fields and compiles them into a specific prompt —
// a well-formed change request instead of a bare starter sentence.
const TEMPLATES = [
  {
    id: 'bug',
    label: 'Bug fix',
    icon: '🐛',
    desc: 'Something broken — what happened vs. what you expected',
    fields: [
      { key: 'what_happened', label: 'What happened', ph: 'clicking Save shows a blank screen' },
      { key: 'expected', label: 'Expected behavior', ph: 'the claim saves and returns to the list' },
      { key: 'where_seen', label: 'Where it appears', ph: '/items, signed-in app' },
    ],
    template: 'Fix this bug. What happened: {what_happened}. Expected behavior: {expected}. Where it appears: {where_seen}. If a screenshot is attached, use it as the source of truth for the visual state.',
  },
  {
    id: 'ui',
    label: 'UI tweak',
    icon: '🎨',
    desc: 'Change how an element looks or behaves',
    fields: [
      { key: 'element', label: 'Element', ph: 'the dashboard KPI cards' },
      { key: 'current', label: 'Current look/behavior', ph: 'four cards in one row, cramped on laptop' },
      { key: 'desired', label: 'Desired', ph: 'two rows of two below 1200px' },
    ],
    template: 'UI change. Element: {element}. Current: {current}. Desired: {desired}. If a screenshot is attached, use it as the source of truth for the visual state.',
  },
  {
    id: 'copy',
    label: 'Copy change',
    icon: '✏️',
    desc: 'Reword text anywhere in the product',
    fields: [
      { key: 'location', label: 'Where', ph: 'home page welcome banner' },
      { key: 'current_copy', label: 'Current text', ph: 'Welcome back' },
      { key: 'new_copy', label: 'New text', ph: 'Good to see you' },
    ],
    template: 'Copy change. Location: {location}. Current text: "{current_copy}". New text: "{new_copy}". Change only the wording, not layout or logic.',
  },
  {
    id: 'feature',
    label: 'New feature',
    icon: '✨',
    desc: 'A user story with acceptance criteria',
    fields: [
      { key: 'user_story', label: 'User story', ph: 'as an admin I want to export an item summary' },
      { key: 'acceptance_criteria', label: 'Acceptance criteria', ph: 'button on the item page, PDF with details + asset list' },
      { key: 'constraints', label: 'Constraints', ph: 'no new backend dependencies' },
    ],
    template: 'Add a feature. User story: {user_story}. Acceptance criteria: {acceptance_criteria}. Constraints: {constraints}.',
  },
  {
    id: 'explain',
    label: 'Explain',
    icon: '❓',
    desc: 'Ask how something works — no code changes',
    fields: [
      { key: 'question', label: 'Question', ph: 'how does the evidence upload path work?' },
    ],
    template: 'Do not change any files. Explain, with file references: {question}',
  },
];

const compileTemplate = (tpl, values) =>
  tpl.template.replace(/\{(\w+)\}/g, (_, k) => (values[k] || '').trim() || `(${k.replace(/_/g, ' ')}: not specified)`);

// Prompt sent when a plan is approved — executes in agent mode (Cursor's
// native Plan mode produced the plan; this follow-up flips to implementation).
const EXECUTE_PROMPT = 'Approved. Execute the plan you produced in this session exactly as written. '
  + 'Push a branch and open a pull request when complete.';

// Plain-language hints under raw failure messages — code/message matching,
// worst case no hint. requestId (when present) is what Cursor support needs.
function failureHint(sess) {
  const m = `${sess?.errorCode || ''} ${sess?.errorMessage || ''}`.toLowerCase();
  if (!m.trim()) return null;
  if (/(^|\D)401(\D|$)|unauthenticated|invalid api key|key rejected/.test(m)) return 'API key rejected — check the key indicator in the sidebar. Rotate SSM /<project-slug>/cursor-api-key and restart the backend if it shows red.';
  if (/(^|\D)403(\D|$)|forbidden|not authorized/.test(m)) return 'The Cursor key\'s account does not have access to this repository.';
  if (/(^|\D)404(\D|$)|agent.*not found|deleted or expired/.test(m)) return 'This agent was deleted or expired on Cursor\'s side — start a fresh session (Retry does this).';
  if (/(^|\D)429(\D|$)|rate limit/.test(m)) return 'Rate limited by Cursor. Wait a minute, then retry.';
  if (/(^|\D)5\d\d(\D|$)|internal error|service unavailable|overloaded/.test(m)) return 'Cursor-side error. Retry usually resolves this.';
  if (/isn't connected|default branch/.test(m)) return null; // message already explains the fix
  return null;
}

// ---- console settings (localStorage) ---------------------------------------------
const SETTINGS_KEY = 'devc-settings';
const loadSettings = () => {
  try { return { notify: false, autoArchive: false, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}') }; }
  catch { return { notify: false, autoArchive: false }; }
};

// ---- artifacts tab -----------------------------------------------------------------
const isImagePath = (p) => /\.(png|jpe?g|gif|webp)$/i.test(p || '');
const fmtBytes = (n) => {
  if (n == null) return '—';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)} kB`;
  return `${n} B`;
};

function ArtifactsPanel({ sessionId, running }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);
  const [previews, setPreviews] = useState({}); // path -> presigned url

  useEffect(() => {
    let alive = true;
    setItems(null); setErr(null); setPreviews({});
    api(`/dev/sessions/${sessionId}/artifacts`)
      .then((d) => { if (alive) setItems(d.items || []); })
      .catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [sessionId]);

  // Links expire in 15 minutes — always fetch fresh at click time.
  const openArtifact = async (path) => {
    try {
      const { url } = await api(`/dev/sessions/${sessionId}/artifacts/link?path=${encodeURIComponent(path)}`);
      window.open(url, '_blank', 'noopener');
    } catch (e) { setErr(e.message); }
  };
  const togglePreview = async (path) => {
    if (previews[path]) { setPreviews((p) => ({ ...p, [path]: null })); return; }
    try {
      const { url } = await api(`/dev/sessions/${sessionId}/artifacts/link?path=${encodeURIComponent(path)}`);
      setPreviews((p) => ({ ...p, [path]: url }));
    } catch (e) { setErr(e.message); }
  };

  if (err) return <div className="devc-err">Artifacts unavailable: {err}</div>;
  if (items === null) return <div className="devc-dim" style={{ padding: 8 }}>Loading artifacts…</div>;
  if (!items.length) {
    return (
      <div className="devc-empty">
        <h3>No artifacts yet</h3>
        <p>
          Images, PDFs and recordings the agent produces, plus anything it saves under an
          <code>artifacts/</code> folder, appear here after the turn finishes. Ask for one in a follow-up:
          &ldquo;attach a screenshot of the change&rdquo; or &ldquo;save the sample images under artifacts/&rdquo;.
          {running ? ' The agent is still working — check back when it finishes.' : ''}
        </p>
      </div>
    );
  }
  return (
    <div className="devc-card">
      <div className="devc-card-title">Artifacts <span className="devc-chip gray">{items.length}</span></div>
      {items.map((a) => (
        <div key={a.path} className="devc-artifact">
          <div className="devc-artifact-row">
            <span className="devc-artifact-ic">{isImagePath(a.path) ? '🖼' : '📄'}</span>
            <span className="devc-artifact-name">{a.path.replace(/^artifacts\//, '')}</span>
            <span className="devc-dim">{fmtBytes(a.sizeBytes)}{a.updatedAt ? ` · ${fmtAgo(new Date(a.updatedAt).getTime())}` : ''}</span>
            {isImagePath(a.path) && (
              <button className="devc-filter" onClick={() => togglePreview(a.path)}>
                {previews[a.path] ? 'Hide' : 'Preview'}
              </button>
            )}
            <button className="devc-filter" onClick={() => openArtifact(a.path)}>Download ↗</button>
          </div>
          {previews[a.path] && <img className="devc-artifact-img" src={previews[a.path]} alt={a.path} />}
        </div>
      ))}
      <div className="devc-dim" style={{ marginTop: 8 }}>Download links are temporary (15 minutes) — a fresh one is minted per click.</div>
    </div>
  );
}

// ---- session feedback (thumbs) ----------------------------------------------------
// The quality signal behind smart routing: up = the tier's work was accepted,
// down = it failed (the next follow-up steps up a tier). Clicking the active
// thumb clears it.
function SessionFeedback({ sessionId, feedback }) {
  const [busy, setBusy] = useState(false);
  const current = feedback?.rating || null;
  const send = async (rating) => {
    if (busy || !sessionId) return;
    setBusy(true);
    try { await api(`/dev/sessions/${sessionId}/feedback`, { rating: current === rating ? null : rating }); } catch { /* the session doc is the truth */ } finally { setBusy(false); }
  };
  return (
    <div className="devc-feedback" role="group" aria-label="Rate this result">
      <span className="devc-dim">Was this result right?</span>
      <button type="button" className={`devc-thumb${current === 'up' ? ' is-on' : ''}`} aria-pressed={current === 'up'} aria-label="Thumbs up" disabled={busy} onClick={() => send('up')}>👍</button>
      <button type="button" className={`devc-thumb${current === 'down' ? ' is-on' : ''}`} aria-pressed={current === 'down'} aria-label="Thumbs down" disabled={busy} onClick={() => send('down')}>👎</button>
    </div>
  );
}

// ---- per-run usage tab --------------------------------------------------------------
function UsagePanel({ sessionId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null); setErr(null);
    api(`/dev/sessions/${sessionId}/usage`)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [sessionId]);

  const costCell = (c) => (c == null || c.chargedCents == null ? '—' : `$${(c.chargedCents / 100).toFixed(3)}`);
  const costReportPath = '/dev/costs';

  if (err) return <div className="devc-err">Usage unavailable: {err}</div>;
  if (!data) return <div className="devc-dim" style={{ padding: 8 }}>Loading usage…</div>;
  return (
    <div className="devc-card">
      <div className="devc-card-title">Token usage by run <span className="devc-chip gray">{data.runs.length} run{data.runs.length === 1 ? '' : 's'}</span></div>
      <table className="devc-usage-table">
        <thead>
          <tr><th>Run</th><th>Input</th><th>Output</th><th>Cache read</th><th>Total</th><th>Cost</th></tr>
        </thead>
        <tbody>
          {data.runs.map((r, i) => (
            <tr key={r.runId}>
              <td title={r.runId}>#{i + 1}</td>
              <td>{fmtTok(r.usage?.inputTokens)}</td>
              <td>{fmtTok(r.usage?.outputTokens)}</td>
              <td>{fmtTok(r.usage?.cacheReadTokens)}</td>
              <td>{fmtTok(r.usage?.totalTokens)}</td>
              <td>{costCell(r.cost)}</td>
            </tr>
          ))}
          <tr className="devc-usage-total">
            <td>Total</td>
            <td>{fmtTok(data.usage?.inputTokens)}</td>
            <td>{fmtTok(data.usage?.outputTokens)}</td>
            <td>{fmtTok(data.usage?.cacheReadTokens)}</td>
            <td>{fmtTok(data.usage?.totalTokens)}</td>
            <td>{costCell(data.cost)}</td>
          </tr>
        </tbody>
      </table>
      <div className="devc-dim" style={{ marginTop: 8 }}>
        {data.source
          ? <>Runner session — {data.source === 'reported' ? 'tokens and cost as reported by the agent CLI' : data.source === 'estimated' ? 'tokens estimated from the bytes exchanged, priced by dev.router.priceUsdPerMtok' : 'a mix of CLI-reported and estimated turns'}.</>
          : <>Cost is Cursor-billed dollars (eventually consistent — recent runs can show &ldquo;—&rdquo; briefly).</>}{' '}
        <a className="devc-link" href={costReportPath}>Team-wide spend: Cost Report →</a>
      </div>
    </div>
  );
}

// ---- workspace views: Config / Access / Ops ---------------------------------------
// Session-independent operator surfaces (sidebar switcher). Read access rides
// the internal claim like the rest of the console; mutations need the admin
// app role (the backend enforces it — the UI just explains the read-only state).

const deepMergeCfg = (base, over) => {
  if (!over || typeof over !== 'object') return base;
  const out = { ...base };
  for (const [k, v] of Object.entries(over)) {
    out[k] = v && typeof v === 'object' && !Array.isArray(v) && base?.[k] && typeof base[k] === 'object'
      ? deepMergeCfg(base[k], v)
      : v;
  }
  return out;
};

// Keep only values that differ from the defaults (so the saved doc stays a
// minimal override set, like it was when hand-edited in the console).
const pruneCfg = (merged, defaults) => {
  const out = {};
  for (const [k, v] of Object.entries(merged || {})) {
    const d = defaults?.[k];
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const sub = pruneCfg(v, d || {});
      if (Object.keys(sub).length) out[k] = sub;
    } else if (JSON.stringify(v) !== JSON.stringify(d)) {
      out[k] = v;
    }
  }
  return out;
};

function ConfigPanel({ isAdmin }) {
  const [data, setData] = useState(null); // { doc, defaults }
  const [merged, setMerged] = useState(null);
  const [rawMode, setRawMode] = useState(false);
  const [rawText, setRawText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { ok, text }

  const load = async () => {
    try {
      const d = await api('/dev/config');
      setData(d);
      setMerged(deepMergeCfg(d.defaults, d.doc));
      setMsg(null);
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (section, key, value) =>
    setMerged((m) => ({ ...m, [section]: { ...m[section], [key]: value } }));

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      let config;
      if (rawMode) {
        config = JSON.parse(rawText); // throws with a readable message
      } else {
        config = pruneCfg(merged, data.defaults);
        // Unknown keys the defaults don't know about (e.g. vaResources) ride
        // along verbatim from the saved doc.
        for (const [k, v] of Object.entries(data.doc || {})) {
          if (!(k in data.defaults)) config[k] = v;
        }
      }
      const res = await api('/dev/config', { config });
      setMsg({ ok: true, text: res.changes.length ? `Saved — ${res.changes.length} value(s) changed. Backends pick it up within ~1 min.` : 'No changes.' });
      await load();
      setRawMode(false);
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  if (!merged) return <div className="devc-empty"><h3>Live config</h3><p>{msg?.text || 'Loading config/app…'}</p></div>;

  return (
    <div className="devc-panel">
      <div className="devc-card">
        <div className="devc-card-title">
          Live config — <code>config/app</code>
          <span className="devc-dim" style={{ fontWeight: 400 }}>
            &nbsp;applies within ~1 minute, no deploy · every save is audited
          </span>
        </div>
        {!isAdmin && <div className="devc-warn">Read-only — changing values needs the admin role.</div>}
        {rawMode ? (
          <>
            <textarea
              className="devc-cfg-raw"
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              spellCheck={false}
            />
            <div className="devc-dim">Raw mode writes the doc exactly as typed (only overrides need to be present — defaults fill the rest).</div>
          </>
        ) : (
          Object.entries(merged).map(([section, fields]) => (
            (fields && typeof fields === 'object' && !Array.isArray(fields)) ? (
              <div className="devc-cfg-section" key={section}>
                <h4>{section}</h4>
                <div className="devc-cfg-grid">
                  {Object.entries(fields).map(([key, value]) => (
                    <label key={key} className="devc-cfg-field">
                      <span>{key}</span>
                      {typeof value === 'boolean' ? (
                        <input type="checkbox" disabled={!isAdmin} checked={value} onChange={(e) => setField(section, key, e.target.checked)} />
                      ) : typeof value === 'number' ? (
                        <input type="number" disabled={!isAdmin} value={value} onChange={(e) => setField(section, key, Number(e.target.value))} />
                      ) : typeof value === 'string' ? (
                        <input type="text" disabled={!isAdmin} value={value} onChange={(e) => setField(section, key, e.target.value)} />
                      ) : (
                        <input type="text" disabled title="Edit nested/complex values in raw mode" value={JSON.stringify(value)} readOnly />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ) : null
          ))
        )}
        <div className="devc-cfg-actions">
          {isAdmin && <button className="devc-btn primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save config'}</button>}
          <button
            className="devc-btn"
            onClick={() => {
              if (!rawMode) setRawText(JSON.stringify(data.doc || {}, null, 2));
              setRawMode(!rawMode);
              setMsg(null);
            }}
          >
            {rawMode ? 'Form mode' : 'Raw JSON'}
          </button>
          <button className="devc-btn" onClick={load}>↻ Reload</button>
          {msg && <span className={msg.ok ? 'devc-ok' : 'devc-err'}>{msg.text}</span>}
        </div>
      </div>
    </div>
  );
}

const APP_ROLES = ['admin', 'product', 'tester', 'waitlist'];

function AccessPanel({ isAdmin }) {
  const [rows, setRows] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [busyEmail, setBusyEmail] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      setRows((await api('/dev/access')).rows);
      setMsg(null);
    } catch (e) {
      setMsg(e.message);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (email, fn) => {
    setBusyEmail(email); setMsg(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusyEmail(null);
    }
  };
  const addEmail = () => act(newEmail, async () => {
    await api('/dev/access/allowlist', { email: newEmail.trim(), action: 'add' });
    setNewEmail('');
  });
  const removeEmail = (email) => act(email, () => api('/dev/access/allowlist', { email, action: 'remove' }));
  const setRole = (email, role) => act(email, () => api('/dev/access/role', { email, role: role || null }));

  return (
    <div className="devc-panel">
      <div className="devc-card">
        <div className="devc-card-title">
          Access — tester allowlist &amp; roles
          <span className="devc-dim" style={{ fontWeight: 400 }}>&nbsp;role changes land on the user&apos;s next sign-in · every change is audited</span>
        </div>
        {!isAdmin && <div className="devc-warn">Read-only — managing access needs the admin role.</div>}
        <p className="devc-muted" style={{ margin: '4px 0 10px', fontSize: 12 }}>
          Roles: <strong>admin</strong> runs the console and changes settings · <strong>product</strong> sees
          everything, changes nothing · <strong>tester</strong> uses the app (the default) · <strong>waitlist</strong> is
          signed up but can&apos;t see pre-launch pages yet.
        </p>
        {isAdmin && (
          <div className="devc-access-add">
            <input
              type="email"
              placeholder="invite email (adds to the signup allowlist)"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && newEmail.includes('@')) addEmail(); }}
            />
            <button className="devc-btn primary" disabled={!newEmail.includes('@') || busyEmail} onClick={addEmail}>+ Allowlist</button>
          </div>
        )}
        {msg && <div className="devc-err">{msg}</div>}
        {rows === null ? (
          <div className="devc-dim" style={{ padding: 10 }}>Loading…</div>
        ) : (
          <table className="devc-access-table">
            <thead>
              <tr><th>Email</th><th>Status</th><th>Role</th><th>Last sign-in</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.email} className={busyEmail === r.email ? 'devc-row-busy' : ''}>
                  <td>
                    {r.email}
                    {r.internal && <span className="devc-chip blue" title="internal claim (Dev Console access) — managed by the owner's scripts only">internal</span>}
                  </td>
                  <td>
                    {r.notAllowlisted
                      ? <span className="devc-chip amber" title="Has an account but is no longer on the signup allowlist">account only</span>
                      : r.hasAccount ? <span className="devc-chip green">active</span> : <span className="devc-chip gray">invited</span>}
                  </td>
                  <td>
                    {r.hasAccount ? (
                      <select
                        disabled={!isAdmin || busyEmail === r.email}
                        value={r.role || ''}
                        onChange={(e) => setRole(r.email, e.target.value)}
                      >
                        <option value="">(none — tester behavior)</option>
                        {APP_ROLES.map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                    ) : <span className="devc-dim">—</span>}
                  </td>
                  <td className="devc-dim">{r.lastSignIn ? fmtAgo(new Date(r.lastSignIn).getTime()) : '—'}</td>
                  <td>
                    {isAdmin && !r.notAllowlisted && (
                      <button className="devc-btn danger" title="Remove from the signup allowlist (existing accounts keep working)" disabled={busyEmail === r.email} onClick={() => removeEmail(r.email)}>✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="devc-dim" style={{ marginTop: 8 }}>
          Allowlist gates <em>signup</em>; removing an email does not disable an existing account.
          The <code>internal</code> claim (Dev Console access itself) is deliberately not manageable here.
        </div>
      </div>
    </div>
  );
}

const LOG_RANGES = [['15m', 15 * 60 * 1000], ['1h', 60 * 60 * 1000], ['6h', 6 * 60 * 60 * 1000], ['24h', 24 * 60 * 60 * 1000]];

// ---- Agents panel: eval runs + the governance trail -----------------------------
// Read-only ops surface for the agent stack: recent eval runs (written by
// scripts/agent-evals.mjs into `evals`) and the latest policy denials from
// the append-only auditEvents trail (shared/policy.js).
function AgentsPanel() {
  const evals = useOrdered('evals', 'createdAt', 'desc', 20);
  const audits = useOrdered('auditEvents', 'createdAt', 'desc', 200);
  const denials = audits.filter((a) => a.decision === 'denied').slice(0, 30);
  const [openRun, setOpenRun] = useState(null);
  const [usage, setUsage] = useState(null);
  const [openTrace, setOpenTrace] = useState(null);

  useEffect(() => {
    api('/dev/ops/agent-usage').then(setUsage).catch(() => setUsage(null));
  }, []);

  return (
    <div className="devc-panel">
      <div className="devc-card">
        <div className="devc-card-title">AI usage — {usage?.period || 'this month'}</div>
        {!usage && <p className="devc-muted">Loading…</p>}
        {usage && Object.keys(usage.counters || {}).length === 0 && (
          <p className="devc-muted">No AI usage metered yet this month.</p>
        )}
        {usage &&
          Object.entries(usage.counters || {})
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([k, v]) => (
              <div key={k} className="devc-muted" style={{ fontSize: 12, padding: '2px 0' }}>
                <code>{k}</code>: {fmtTok(v)}
              </div>
            ))}
      </div>

      <div className="devc-card">
        <div className="devc-card-title">Recent turn traces</div>
        {(!usage || !(usage.traces || []).length) && <p className="devc-muted">No traced turns yet.</p>}
        {(usage?.traces || []).map((t) => (
          <div key={t.id} style={{ marginBottom: 4 }}>
            <button
              className="devc-filter"
              style={{ width: '100%', textAlign: 'left' }}
              onClick={() => setOpenTrace(openTrace === t.id ? null : t.id)}
            >
              {fmtAgo(t.at)} · {t.status} · {fmtTok(t.tokens)} tok · {fmtDur(t.ms)} ({fmtDur(t.modelMs)} model) — {t.content}
            </button>
            {openTrace === t.id && (
              <div style={{ padding: '4px 10px', fontSize: 12 }}>
                {(t.spans || []).map((s, i) => (
                  <div key={i} className="devc-muted" style={{ padding: '1px 0' }}>
                    {s.kind === 'model'
                      ? `🧠 ${s.stage} · ${s.provider}/${s.model} · ${fmtDur(s.ms)} · ${fmtTok(s.tokens)} tok`
                      : `${s.denied ? '🚫' : s.ok ? '🔧' : '⚠️'} ${s.tool} · ${fmtDur(s.ms)}`}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="devc-card">
        <div className="devc-card-title">Agent evals</div>
        {evals.length === 0 && (
          <p className="devc-muted">
            No eval runs yet. Evals score your agents against a golden question set and an adversarial red-team set;
            results land here.
            <details style={{ marginTop: 6 }}>
              <summary className="devc-dim">Advanced: run from the command line</summary>
              <code>node scripts/agent-evals.mjs</code>
            </details>
          </p>
        )}
        {evals.map((e) => (
          <div key={e.id} style={{ marginBottom: 6 }}>
            <button
              className="devc-filter"
              style={{ width: '100%', textAlign: 'left' }}
              onClick={() => setOpenRun(openRun === e.id ? null : e.id)}
            >
              {e.summary?.ok ? '✅' : '❌'} {fmtAgo(e.runAt)} · golden {e.summary?.goldenPass}/{e.summary?.goldenTotal}
              {e.summary?.goldenSkipped ? ` (${e.summary.goldenSkipped} skipped)` : ''} · red team{' '}
              {e.summary?.advPass}/{e.summary?.advTotal} · provider {e.provider}
            </button>
            {openRun === e.id && (
              <div style={{ padding: '6px 10px', fontSize: 12 }}>
                {[...(e.golden || []), ...(e.adversarial || [])].map((r) => (
                  <div key={r.id} className="devc-muted" style={{ padding: '2px 0' }}>
                    {r.pass === null ? '⏭' : r.pass ? '✓' : '✗'} {r.id}
                    {typeof r.score === 'number' ? ` (${r.score})` : ''} — {r.reason}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="devc-card">
        <div className="devc-card-title">Recent policy denials ({denials.length})</div>
        {denials.length === 0 && <p className="devc-muted">No denials in the recent trail.</p>}
        {denials.map((a) => (
          <div key={a.id} className="devc-muted" style={{ fontSize: 12, padding: '3px 0' }}>
            🚫 {fmtAgo(a.createdAt)} · {a.surface} · <code>{a.tool}</code>
            {a.actor?.agentId ? ` · agent ${a.actor.agentId}` : a.actor?.userId ? ` · user ${a.actor.userId.slice(0, 8)}` : ''}
            {a.reason ? ` — ${a.reason}` : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Ecosystem (live 3D view) ------------------------------------------------
// One poll of /dev/ops/ecosystem every 30s while the tab is visible, shared by
// the full view and the hero widget. Attention is carried by the scene's
// color + motion and the one-line caption — no badges, sounds, or modals.
function useEcosystemFeed(enabled = true) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let stop = false;
    let t = null;
    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      try { const out = await api('/dev/ops/ecosystem'); if (!stop) { setData(out); setErr(null); } } catch (e) { if (!stop) setErr(e.message); }
    };
    tick();
    t = setInterval(tick, 30000);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { stop = true; clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [enabled]);
  return { data, err };
}

const LEVEL_CHIP = { ok: 'green', warn: 'amber', down: 'red', off: 'gray' };

function EcosystemPanel() {
  const { data, err } = useEcosystemFeed(true);
  const s = useMemo(() => deriveEcosystem(data), [data]);
  const plane = data?.plane && !data.plane.unreachable ? data.plane : null;
  const cmd = useCommandToggle('devconsole');
  const cs = useMemo(() => deriveCommand(data), [data]);
  // Telemetry: the control plane's edge series for this instance + twin, the
  // local runtime block (queues / stale claims / users), or the simulator
  // over the same two-node roster. Only on while Command mode is up.
  const slug = data?.slug || s.viewer.slug || '';
  const hasTwin = !!plane?.twin?.exists;
  const roster = useMemo(() => (slug ? [{ slug, twinSlug: hasTwin ? `${slug}-uat` : null }, ...(hasTwin ? [{ slug: `${slug}-uat`, envRole: 'uat' }] : [])] : []), [slug, hasTwin]);
  const fetchReal = useCallback((w) => api(`/dev/ops/telemetry?window=${w}`), []);
  const { telemetry, controls } = useTelemetry({ surface: 'devconsole', roster, fetchReal, focusSlug: slug, enabled: cmd.on && !!data });
  const panels = useMemo(() => (data ? [...appPanels(cs, { isAdmin: true }), trafficPanel(telemetry, { side: 'left', id: 'ap-traffic' }), anomalyPanel(telemetry, { side: 'right', id: 'ap-anomalies' })] : []), [data, cs, telemetry]);
  const classic = (
    <>
      {err && <div className="devc-dim" style={{ marginTop: 6 }}>feed error: {err}</div>}
      {s.viewer.role === 'uat' && (
        <div className="devc-eco-here">
          You are on the <strong>UAT twin</strong>{s.viewer.name ? <> (<code>{s.viewer.slug}</code>)</> : null} — the picture below is the whole pair; production is the centre, this host is the twin.
        </div>
      )}
      <div className="devc-eco-legend devc-dim">
        <span><i className="eco-dot is-ok" /> gold = healthy</span>
        <span><i className="eco-dot is-warn" /> amber = needs a look</span>
        <span><i className="eco-dot is-down" /> red = down</span>
        <span>The ring pulses each time the uptime monitor checks · sparks mean a security scan is running · a light travelling along a line is a deploy in progress.</span>
      </div>
      <div className="devc-eco-grid">
        <div className="devc-card">
          <div className="devc-card-title">Services <span className="devc-dim" style={{ fontWeight: 400 }}>{data?.runtime === 'docker' ? 'docker host' : 'ECS'}</span></div>
          {s.services.length ? (
            <table className="devc-eco-table">
              <tbody>
                {s.services.map((x) => (
                  <tr key={x.name}>
                    <td><span className={`devc-chip ${LEVEL_CHIP[x.level]}`}>{x.level === 'off' ? 'idle' : x.level}</span></td>
                    <td>{x.name}{x.kind === 'runner' && <span className="devc-dim"> · runner</span>}</td>
                    <td className="devc-dim">{x.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="devc-dim">{data ? 'No services reported.' : 'Loading…'}</div>}
        </div>
        <div className="devc-card">
          <div className="devc-card-title">Control plane</div>
          {plane ? (
            <div className="devc-eco-kv">
              <div><span className="devc-dim">{s.viewer.role === 'uat' ? 'production' : 'instance'}</span><span>{plane.instance?.name || plane.instance?.slug} · {plane.instance?.status}{plane.instance?.version ? ` · ${plane.instance.version}` : ''}</span></div>
              <div><span className="devc-dim">monitor</span><span><span className={`devc-chip ${LEVEL_CHIP[s.monitor.level]}`}>{plane.monitor?.state || 'unknown'}</span>{plane.monitor?.lastCheckAt ? ` ${fmtAgo(plane.monitor.lastCheckAt)}` : ''}{plane.monitor?.lastLatencyMs != null ? ` · ${plane.monitor.lastLatencyMs}ms` : ''}{plane.monitor?.tlsDaysLeft != null ? ` · TLS ${plane.monitor.tlsDaysLeft}d` : ''}</span></div>
              <div><span className="devc-dim">{s.viewer.role === 'uat' ? 'uat twin (this host)' : 'uat twin'}</span><span>{plane.twin?.exists ? <>{plane.twin.status}{plane.twin.lastDeploy ? ` · last deploy ${plane.twin.lastDeploy.status} ${fmtAgo(plane.twin.lastDeploy.at)}` : ''}</> : 'none'}</span></div>
              <div><span className="devc-dim">prod deploy</span><span>{plane.prod?.activeDeploy ? `${plane.prod.activeDeploy.status} · ${plane.prod.activeDeploy.stage || ''}` : plane.prod?.lastDeploy ? `${plane.prod.lastDeploy.status} ${fmtAgo(plane.prod.lastDeploy.at)}` : '—'}</span></div>
              <div><span className="devc-dim">security</span><span><span className={`devc-chip ${LEVEL_CHIP[s.security.level]}`}>{s.security.scanning ? 'scanning' : s.security.level === 'off' ? 'no scan' : s.security.level}</span>{plane.scan?.lastScanAt ? ` ${fmtAgo(plane.scan.lastScanAt)} · ${plane.scan.counts?.critical || 0} critical · ${plane.scan.counts?.high || 0} high · ${plane.scan.counts?.medium || 0} medium` : ''}</span></div>
              <div><span className="devc-dim">incidents</span><span>{plane.incidents?.length ? plane.incidents.map((i) => `${i.status} ${fmtAgo(i.openedAt)}${i.reason ? ` (${i.reason})` : ''}`).join(' · ') : 'none in 24h'}</span></div>
              <div><span className="devc-dim">updates</span><span>{plane.updates?.versionBehind ? `kit ${plane.updates.latestKitVersion || 'update'} available` : 'up to date'}</span></div>
              <div><span className="devc-dim">edge</span><span>{plane.edge?.domains?.length ? plane.edge.domains.map((d) => d.host).join(' · ') : '—'}</span></div>
              <div><span className="devc-dim">license</span><span>{data.license?.state || '—'}{data.license?.graceUntil ? ` · grace until ${new Date(data.license.graceUntil).toLocaleDateString()}` : ''}</span></div>
            </div>
          ) : (
            <div className="devc-dim">
              {!data ? 'Loading…' : data.license?.state === 'dormant' || !data.license
                ? 'No control plane attached (LICENSE_KEY / CONTROL_PLANE_URL unset) — showing local services only.'
                : 'Control plane unreachable — showing local services; monitor, twin, scan and edge data resume when it answers.'}
            </div>
          )}
        </div>
      </div>
      <div className="devc-card">
        <div className="devc-card-title">Recent activity</div>
        {s.events.length ? <EcosystemTicker events={s.events} limit={30} /> : <div className="devc-dim">{data ? 'Quiet.' : 'Loading…'}</div>}
      </div>
    </>
  );
  // Command mode: the one-screen stage; every HUD panel opens the classic
  // cards in the drawer (same data, same words).
  const drawer = useMemo(() => {
    const all = { title: 'Ecosystem detail', render: () => <div className="devc-eco-panel">{classic}</div> };
    const costs = {
      title: `Costs · ${data?.period || ''}`,
      render: () => (data?.costs ? (
        <table className="devc-eco-table"><tbody>
          {Object.entries(data.costs.byService || {}).map(([k, v]) => <tr key={k}><td>{k}</td><td className="devc-dim">${v.toFixed(2)}</td></tr>)}
          <tr><td><strong>month to date</strong></td><td><strong>${data.costs.mtdUsd.toFixed(2)}</strong></td></tr>
          <tr><td>burn / day</td><td className="devc-dim">${data.costs.burnPerDayUsd.toFixed(2)}</td></tr>
          <tr><td>projected</td><td className="devc-dim">${data.costs.projectedUsd.toFixed(2)}</td></tr>
        </tbody></table>
      ) : <div className="devc-dim">No AWS cost data yet — the cost worker pulls Cost Explorer daily.</div>),
    };
    const usage = {
      title: `Usage · ${data?.period || ''}`,
      render: () => (data?.usage ? (
        <table className="devc-eco-table"><tbody>
          {Object.entries(data.usage.counters || {}).filter(([k]) => !k.startsWith('tok_')).sort((a, b) => b[1] - a[1]).map(([k, v]) => <tr key={k}><td>{k}</td><td className="devc-dim">{Number(v).toLocaleString()}</td></tr>)}
        </tbody></table>
      ) : <div className="devc-dim">No metered usage this month.</div>),
    };
    const traffic = {
      title: `Traffic & flows · ${telemetry.window.label}`,
      render: () => (
        <>
          {slug && <div className="cmd-panel-body" style={{ marginBottom: 8 }}><RuntimeRows t={telemetry} slug={slug} /></div>}
          <FlowTable t={telemetry} />
        </>
      ),
    };
    const anomalies = { title: `Anomalies · ${telemetry.window.label}`, render: () => <AnomalyTable t={telemetry} /> };
    return { 'ap-identity': all, 'ap-health': all, 'ap-services': all, 'ap-eco': all, 'ap-security': all, 'ap-envs': all, 'ap-costs': costs, 'ap-usage': usage, 'ap-traffic': traffic, 'ap-anomalies': anomalies };
  }, [data, err, telemetry, slug]); // eslint-disable-line react-hooks/exhaustive-deps -- classic derives from data/err

  return (
    <div className={`devc-panel devc-eco-panel ${cmd.on ? 'is-command' : ''}`}>
      {cmd.supported && <div className="devc-eco-toggle"><CommandToggle on={cmd.on} onChange={cmd.set} /></div>}
      {cmd.on ? (
        <CommandView data={data} derive={deriveCommand} scene="command" panels={panels} drawer={drawer} events={cs.events || []} caption={data ? cs.caption : (err ? `feed error: ${err}` : 'loading…')} className="is-devc" telemetry={telemetry}>
          <TelemetryControls controls={controls} compact />
          <ActivityStrip telemetry={telemetry} />
        </CommandView>
      ) : (
        <>
          <EcosystemFrame data={data} ticker={false} />
          {classic}
        </>
      )}
    </div>
  );
}

function EcosystemWidget({ onOpen }) {
  const { data } = useEcosystemFeed(true);
  return (
    <div className="devc-eco-widget">
      <EcosystemFrame data={data} compact onClick={onOpen} />
    </div>
  );
}

function OpsPanel() {
  const [health, setHealth] = useState(null);
  const [group, setGroup] = useState('backend');
  const [range, setRange] = useState(15 * 60 * 1000);
  const [filter, setFilter] = useState('');
  const [events, setEvents] = useState(null);
  const [auto, setAuto] = useState(false);
  const [err, setErr] = useState(null);
  const logEnd = useRef(null);

  const loadHealth = async () => {
    try { setHealth(await api('/dev/ops/health')); } catch (e) { setErr(e.message); }
  };
  const loadLogs = async () => {
    try {
      const qs = new URLSearchParams({ group, sinceMs: String(range), ...(filter ? { filter } : {}) });
      const out = await api(`/dev/ops/logs?${qs}`);
      setEvents(out.events);
      setErr(null);
      setTimeout(() => logEnd.current?.scrollIntoView({ block: 'end' }), 50);
    } catch (e) {
      setErr(e.message);
    }
  };
  useEffect(() => { loadHealth(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadLogs(); }, [group, range]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!auto) return undefined;
    const t = setInterval(() => { loadLogs(); loadHealth(); }, 15000);
    return () => clearInterval(t);
  }, [auto, group, range, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="devc-panel">
      <div className="devc-card">
        <div className="devc-card-title">
          Service health
          {health && <button className="devc-btn" style={{ marginLeft: 8 }} onClick={loadHealth}>↻</button>}
        </div>
        {health ? (
          (health.tiers || [health]).map((t) => (
            <div className="devc-ops-health" key={t.service}>
              <span className={`devc-chip ${t.env === 'uat' ? 'blue' : 'gray'}`}>{(t.env || 'prod').toUpperCase()}</span>
              <span className={`devc-chip ${t.rollout === 'COMPLETED' ? 'green' : t.rollout === 'FAILED' ? 'red' : 'blue'}`}>
                {t.rollout || t.status}
              </span>
              <span>{t.service}</span>
              <span className="devc-dim">running {t.running}/{t.desired}{t.pending ? ` (+${t.pending} pending)` : ''}</span>
              <span className="devc-dim">task-def {t.taskDefinition}</span>
              {t.deployedAt && <span className="devc-dim">last deploy {fmtAgo(new Date(t.deployedAt).getTime())}</span>}
            </div>
          ))
        ) : <div className="devc-dim">Loading…</div>}
        {health?.events?.length > 0 && (
          <details className="devc-ops-events">
            <summary className="devc-dim">Recent service events (this environment)</summary>
            {health.events.map((e, i) => (
              <div key={i} className="devc-dim">{fmtAgo(new Date(e.at).getTime())} — {e.text}</div>
            ))}
          </details>
        )}
      </div>
      <div className="devc-card devc-ops-logcard">
        <div className="devc-card-title">
          CloudWatch logs
          <span className="devc-dim" style={{ fontWeight: 400 }}>&nbsp;read-only diagnostics for this environment</span>
        </div>
        <div className="devc-ops-controls">
          <div className="devc-filters">
            {[['backend', 'API + workers'], ['runner', 'Deploy/job runners']].map(([g, label]) => (
              <button key={g} className={`devc-filter${group === g ? ' active' : ''}`} onClick={() => setGroup(g)}>{label}</button>
            ))}
          </div>
          <div className="devc-filters">
            {LOG_RANGES.map(([label, ms]) => (
              <button key={label} className={`devc-filter${range === ms ? ' active' : ''}`} onClick={() => setRange(ms)}>{label}</button>
            ))}
          </div>
          <input
            className="devc-search devc-ops-filter"
            placeholder='filter (CloudWatch syntax, e.g. "error")'
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') loadLogs(); }}
          />
          <button className="devc-btn" onClick={loadLogs}>↻ Refresh</button>
          <label className="devc-dim devc-tab-opt">
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> auto (15s)
          </label>
        </div>
        {err && <div className="devc-err">{err}</div>}
        <div className="devc-ops-logs">
          {events === null ? (
            <div className="devc-dim">Loading…</div>
          ) : events.length ? (
            events.map((e, i) => (
              <div key={i} className="devc-ops-line">
                <span className="devc-dim">{new Date(e.ts).toLocaleTimeString()}</span> {e.text}
              </div>
            ))
          ) : (
            <div className="devc-dim">No log events in this window{filter ? ' matching the filter' : ''}.</div>
          )}
          <div ref={logEnd} />
        </div>
      </div>
    </div>
  );
}

// ---- main page -------------------------------------------------------------------
const TABS = [
  ['chat', 'Chat'],
  ['activity', 'Activity'],
  ['terminal', 'Terminal'],
  ['pr', 'PR & Deploy'],
  ['preview', 'Preview'],
  ['artifacts', 'Artifacts'],
  ['usage', 'Usage'],
  ['details', 'Details'],
];

export default function DevConsole() {
  const { internal, role } = useClaims();
  const [selected, setSelected] = useState(null); // session id | null (new)
  const [view, setView] = useState('sessions'); // sessions | config | access | ops | agents
  const [tab, setTab] = useState('chat');
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState('auto'); // 'auto' (console decides) | SDK AgentModeOption
  const [model, setModel] = useState(''); // '' = account default / keep current
  const [modelParams, setModelParams] = useState({}); // paramId -> value for the picked model
  const [agentOpts, setAgentOpts] = useState(DEFAULT_AGENT_OPTIONS);
  const [files, setFiles] = useState([]); // [{ file, relPath }] — attachments incl. whole folders
  const [upload, setUpload] = useState(null); // live upload progress for the tray
  const [notice, setNotice] = useState(''); // non-fatal attachment note (skipped folders, unreachable pasted image)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [setup, setSetup] = useState(null); // /dev/status result
  const [setupFailed, setSetupFailed] = useState(false); // /dev/status request itself failed (network/CORS/backend down)
  const [statusBusy, setStatusBusy] = useState(false); // model-catalog Retry in flight
  const [showThinking, setShowThinking] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | live | finished | failed
  const [editingTitle, setEditingTitle] = useState(null); // string while editing
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [termFilter, setTermFilter] = useState('all'); // all | agent | deploy
  const [autoScroll, setAutoScroll] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [retryFamily, setRetryFamily] = useState(null); // groups retries into one thread
  const [reportEnvelope, setReportEnvelope] = useState(null); // element-picker capture
  const [activeTemplate, setActiveTemplate] = useState(null); // structured chip id
  const [fieldValues, setFieldValues] = useState({});
  // Per-stage model for plan sessions: the execution run can use a different
  // (typically cheaper) model than the planning run. '' = keep the plan model.
  const [execModel, setExecModel] = useState('');
  const [execParams, setExecParams] = useState({});
  const [settings, setSettings] = useState(loadSettings);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const dragDepth = useRef(0); // dragenter/leave fire per child — count nesting
  const fileInput = useRef(null);
  const folderInput = useRef(null);
  const uploadAbort = useRef(null);
  const chatEnd = useRef(null);
  const termEnd = useRef(null);
  const composerInput = useRef(null);
  const prevStatuses = useRef(new Map()); // notification edge detection
  const sweptRef = useRef(new Set()); // auto-archive one-shot guard

  const saveSettings = (patch) => setSettings((prev) => {
    const next = { ...prev, ...patch };
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch { /* private mode */ }
    return next;
  });

  const sessions = useOrdered(internal ? 'devSessions' : null, 'createdAt', 'desc', 50);
  const session = useDoc(selected ? `devSessions/${selected}` : null);
  const events = useOrdered(selected ? `devSessions/${selected}/events` : null, 'seq', 'asc', 1000);
  const jobLogs = useOrdered(session?.deployJobId ? `devJobs/${session.deployJobId}/logs` : null, 'seq', 'asc', 1000);

  const running = session && LIVE.includes(session.status);
  useTick(!!running || sessions.some((s) => LIVE.includes(s.status)));

  useEffect(() => {
    if (!internal) return;
    api('/dev/status')
      .then((s) => { setSetup(s); setSetupFailed(false); })
      .catch(() => { setSetup(null); setSetupFailed(true); });
  }, [internal]);

  // Manual model-catalog retry: ?refresh=1 bypasses the backend's status
  // cache so a transient Cursor API failure doesn't leave the picker empty.
  // Also the recovery path when /dev/status itself was unreachable.
  const refreshStatus = async () => {
    setStatusBusy(true);
    try {
      setSetup(await api('/dev/status?refresh=1'));
      setSetupFailed(false);
    } catch {
      setSetupFailed(true); // keep whatever setup we had
    } finally {
      setStatusBusy(false);
    }
  };

  // Element-picker handoff: consume a stashed report (envelope + screenshot)
  // and pre-fill the composer with the captured facts.
  useEffect(() => {
    if (!internal) return;
    const report = takeReport();
    if (!report) return;
    const el = report.envelope?.element;
    if (el) {
      setPrompt(
        `On ${report.envelope.route}, the element below needs a change: <describe expected vs. actual>\n\n`
        + `Element: ${el.component ? `${el.component} — ` : ''}${el.domPath}\n`
        + `Size: ${el.boundingBox.width}×${el.boundingBox.height}px, font-size ${el.computedStyles['font-size']}`
      );
    }
    setReportEnvelope(report.envelope || null);
    if (report.screenshot) {
      fetch(report.screenshot)
        .then((r) => r.blob())
        .then((blob) => setFiles([new File([blob], `report-${Date.now()}.jpg`, { type: 'image/jpeg' })]))
        .catch(() => {});
    }
    setSelected(null);
  }, [internal]);

  // Reset per-session UI state on switch. Model/params go back to "no
  // override" — follow-up sends keep the conversation's current (sticky)
  // selection unless the user picks one.
  useEffect(() => {
    setEditingTitle(null); setConfirmDelete(false); setError(null);
    setModel(''); setModelParams({});
    setExecModel(''); setExecParams({});
    setActiveTemplate(null); setFieldValues({});
  }, [selected]);

  // Follow the session's conversation mode (last mode a send used).
  useEffect(() => {
    setMode((selected && session?.mode) || 'auto');
  }, [selected, session?.mode]);

  // Restore the last-used mode/model whenever the NEW-session composer is
  // shown and the live catalog is known. restoreModelPick() re-validates
  // against the catalog: a model that is no longer available is dropped
  // (falls back to the account default) and stale param values reset to that
  // model's defaults. Runs after the reset effects above (definition order),
  // so the restore wins on the new-session composer only.
  useEffect(() => {
    if (selected || !setup?.models?.length) return;
    const stored = loadStoredConfig();
    if (stored.mode && (setup.modes || FALLBACK_MODES).includes(stored.mode)) setMode(stored.mode);
    const pick = restoreModelPick(setup.models, stored);
    if (pick) { setModel(pick.id); setModelParams(pick.picks); }
    else { setModel(''); setModelParams({}); } // stored model vanished — account default
  }, [selected, setup]);

  const modelDef = useMemo(
    () => setup?.models?.find((m) => m.id === model) || null,
    [setup, model]
  );

  // The execution-model override is a per-send SendOptions.model, which is
  // sticky — follow-ups on the executed session keep the execution model.
  const execModelDef = useMemo(
    () => setup?.models?.find((m) => m.id === execModel) || null,
    [setup, execModel]
  );
  const pickExecModel = (id) => {
    setExecModel(id);
    setExecParams(defaultParamsFor(setup?.models?.find((m) => m.id === id)));
  };

  // Plan review state: the plan events a plan-mode run wrote (a Revise
  // follow-up appends a new one — the LATEST is what executes), and the
  // model Approve & Execute will actually send (the "Execute with" override,
  // else the session's sticky model), resolved against the live catalog so
  // an invalid pick is caught at review time instead of at execution time.
  const planEvents = useMemo(() => events.filter((e) => e.type === 'plan'), [events]);
  // Thumbs render on the newest result only — one verdict per session.
  const lastResultId = useMemo(() => { const r = events.filter((e) => e.type === 'result'); return r.length ? r[r.length - 1].id : null; }, [events]);
  const latestPlan = planEvents.length ? planEvents[planEvents.length - 1] : null;
  const planPending = session?.mode === 'plan' && !session?.planExecuted;
  const execTargetRaw = execModel || session?.model || '';
  const execTargetDef = useMemo(
    () => findCatalogModel(setup?.models, execTargetRaw),
    [setup, execTargetRaw]
  );
  // Only block when the catalog is loaded AND positively lacks the model —
  // a missing catalog must not freeze approvals (Cursor stays the authority).
  const execTargetInvalid = !!(setup?.models?.length && execTargetRaw && !execTargetDef);
  const execTargetId = execTargetDef?.id || execTargetRaw;
  const canApprovePlan = !!latestPlan?.text && !execTargetInvalid;

  // Pick handlers persist the choice (new-session composer only — follow-up
  // overrides are per-conversation, not a browser preference). A model change
  // resets params to that model's defaults (catalog default variant, else
  // each parameter's first allowed value).
  const pickMode = (m) => {
    setMode(m);
    if (!selected) saveStoredConfig({ mode: m });
  };
  const pickModel = (id) => {
    const picks = defaultParamsFor(setup?.models?.find((m) => m.id === id));
    setModel(id);
    setModelParams(picks);
    if (!selected) saveStoredConfig({ model: id || null, params: id ? picks : null });
  };
  const pickParams = (picks) => {
    setModelParams(picks);
    if (!selected) saveStoredConfig({ params: picks });
  };

  // Browser notifications: fire when a session leaves a live state while the
  // console is open (background tab included). Edge-triggered off the
  // realtime session list — no polling.
  useEffect(() => {
    const prev = prevStatuses.current;
    sessions.forEach((s) => {
      const was = prev.get(s.id);
      if (
        was && LIVE.includes(was) && !LIVE.includes(s.status)
        && settings.notify && typeof Notification !== 'undefined' && Notification.permission === 'granted'
      ) {
        const [label] = CHIP[s.status] || [s.status];
        try {
          const n = new Notification(`${s.title || 'Dev session'}: ${label}`, {
            tag: `devc-${s.id}`,
            body: s.prUrl ? 'A pull request is ready to review.' : undefined,
          });
          n.onclick = () => { window.focus(); setSelected(s.id); n.close(); };
        } catch { /* notification constructor can throw on some platforms */ }
      }
      prev.set(s.id, s.status);
    });
  }, [sessions, settings.notify]);

  // Auto-archive failed sessions older than 24h (opt-in, client-side sweep on
  // the live list — no background job needed).
  useEffect(() => {
    if (!settings.autoArchive) return;
    sessions.forEach((s) => {
      const age = Date.now() - (s.updatedAt || s.createdAt || Date.now());
      if (!s.archived && FAILED.includes(s.status) && age > 24 * 3600 * 1000 && !sweptRef.current.has(s.id)) {
        sweptRef.current.add(s.id);
        api(`/dev/sessions/${s.id}/archive`, {}).catch(() => sweptRef.current.delete(s.id));
      }
    });
  }, [sessions, settings.autoArchive]);

  // Terminal feed: agent shell output + deploy runner logs, chronological.
  const terminal = useMemo(() => {
    const rows = [];
    if (termFilter !== 'deploy') {
      events.forEach((e) => {
        if (e.type !== 'tool') return;
        const mark = e.status === 'running' ? '▶' : (e.status === 'error' ? '✗' : '✔');
        const arg = toolArg(e);
        if (e.name === 'shell') {
          rows.push({ ts: e.ts, text: `${mark} $ ${arg || e.args || ''}${e.result ? `\n${e.result}` : ''}` });
        } else {
          // One line per call: "✔ read src/App.js · 54 lines". Results that
          // are more than a summary go on their own lines.
          const r = String(e.result || '');
          const inline = r && !r.includes('\n') && r.length <= 120;
          rows.push({ ts: e.ts, text: `${mark} ${e.name}${arg ? ` ${arg}` : ''}${inline ? ` · ${r}` : ''}${r && !inline ? `\n${r}` : ''}` });
        }
      });
    }
    if (termFilter !== 'agent') {
      jobLogs.forEach((l) => rows.push({ ts: l.ts, text: l.text, deploy: true }));
    }
    return rows.sort((a, b) => a.ts - b.ts);
  }, [events, jobLogs, termFilter]);

  const lastEventTs = events.length ? events[events.length - 1].ts : session?.updatedAt;
  // The newest streaming thinking/assistant fragment, shown under the
  // working row so a long tool-less stretch still reads as progress.
  const liveThought = useMemo(() => {
    if (!running) return '';
    const last = [...events].reverse().find((e) => e.type === 'thinking' || e.type === 'assistant');
    if (!last || Date.now() - last.ts > 60000) return '';
    const t = String(last.text || '').trim().replace(/\s+/g, ' ');
    return t.length > 160 ? `…${t.slice(-160)}` : t;
  }, [events, running]);

  // Liveness = newest of the last event, the runner heartbeat and the last
  // tool tally — a quiet agent mid-tool-call still counts as alive.
  const liveTs = Math.max(lastEventTs || 0, session?.heartbeatAt || 0, session?.updatedAt || 0) || null;
  const stalled = !!(running && liveTs && Date.now() - liveTs > STALL_MS);

  // Chat view: fold runs of consecutive tool events into one group row so
  // the conversation stays readable while still showing what the agent did.
  const groupedEvents = useMemo(() => {
    const out = [];
    for (const e of events) {
      if (e.type === 'tool') {
        const prev = out[out.length - 1];
        if (prev && prev.type === 'toolgroup') prev.items.push(e);
        else out.push({ type: 'toolgroup', id: `tg-${e.id}`, ts: e.ts, items: [e] });
      } else {
        out.push(e);
      }
    }
    return out;
  }, [events]);

  useEffect(() => { if (autoScroll) chatEnd.current?.scrollIntoView({ block: 'end' }); }, [events.length, autoScroll, tab]);
  useEffect(() => { if (autoScroll) termEnd.current?.scrollIntoView({ block: 'end' }); }, [terminal.length, autoScroll, tab]);

  // Filtered sidebar list, with retry families collapsed to their newest
  // attempt (sessions arrive newest-first) — no more stacked failure cards.
  const visibleSessions = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = sessions.filter((s) => {
      if (q && !(s.title || '').toLowerCase().includes(q)) return false;
      // Archived sessions live under their own filter; everywhere else they
      // stay out of the way.
      if (statusFilter === 'archived') return !!s.archived;
      if (s.archived) return false;
      if (statusFilter === 'live') return LIVE.includes(s.status);
      if (statusFilter === 'finished') return s.status === 'finished';
      if (statusFilter === 'failed') return FAILED.includes(s.status);
      return true;
    });
    const byFamily = new Map();
    const out = [];
    for (const s of filtered) {
      const fam = s.familyId || s.id;
      if (byFamily.has(fam)) { byFamily.get(fam).attempts += 1; continue; }
      const row = { ...s, attempts: 1 };
      byFamily.set(fam, row);
      out.push(row);
    }
    return out;
  }, [sessions, search, statusFilter]);

  // All attempts in the selected session's family (oldest first), for the
  // attempt strip above the tabs.
  const familyAttempts = useMemo(() => {
    if (!session) return [];
    const fam = session.familyId || selected;
    return sessions.filter((s) => (s.familyId || s.id) === fam).sort((a, b) => a.createdAt - b.createdAt);
  }, [sessions, session, selected]);

  if (!internal) {
    return (
      <div className="devc-gate">
        <h2>Dev Console</h2>
        <p>This page is limited to internal accounts. Ask an admin to grant the <code>internal: true</code> custom claim.</p>
      </div>
    );
  }

  // ---- actions ------------------------------------------------------------------
  // A structured template counts as content once any of its fields is filled.
  const templateReady = !selected && activeTemplate
    && Object.values(fieldValues).some((v) => (v || '').trim());
  const canSend = !!prompt.trim() || templateReady;

  const send = async () => {
    if (!canSend || busy) return;
    // Compile the active template's fields plus any free text into the final
    // prompt — the structured part leads, extra context follows.
    let text = prompt.trim();
    if (!selected && activeTemplate) {
      const tpl = TEMPLATES.find((t) => t.id === activeTemplate);
      if (tpl) text = compileTemplate(tpl, fieldValues) + (text ? `\n\nAdditional context: ${text}` : '');
    }
    setBusy(true); setError(null);
    try {
      // Attachments: screenshots → SDK image blocks, documents → extracted
      // text, folders/overflow → a data package (manifest the backend reads,
      // staged in the runner's workspace). Small files go one presigned PUT
      // each; big ones ride the resumable multipart path.
      let images = []; let docs = []; let pkg = null;
      if (files.length) {
        if (!isUploadConfigured()) throw new Error('Upload API not configured');
        const ctl = new AbortController();
        uploadAbort.current = ctl;
        const out = await uploadAttachments(files, {
          prefix: `items/dev/${selected || 'new'}/`,
          signal: ctl.signal,
          onProgress: (p) => setUpload({ ...p, onCancel: () => ctl.abort() }),
        });
        uploadAbort.current = null;
        setUpload(null);
        images = out.images; docs = out.files; pkg = out.package;
        if (out.failures?.length) setNotice(`${out.failures.length} file${out.failures.length > 1 ? 's' : ''} failed to upload and were left out (${out.failures[0].path}: ${out.failures[0].error}).`);
      }
      // Model override carries the per-model params (effort level etc.);
      // params always ride along explicitly — omitted params silently fall
      // back to each parameter's first allowed value on Cursor's side.
      const modelPayload = model
        ? { model, modelParams: buildParamsArray(modelDef, modelParams) }
        : {};
      if (selected) {
        // `mode` follows the session's conversation mode, so Revise
        // follow-ups on an unexecuted plan session stay in plan mode.
        await api(`/dev/sessions/${selected}/messages`, {
          prompt: text, images, files: docs, ...(pkg ? { package: pkg } : {}), mode: mode === 'auto' ? (session?.mode || 'agent') : mode, ...modelPayload,
        });
      } else {
        // New sessions carry the context envelope (context.json v1): route,
        // build SHA, viewport, scrubbed console/network ring buffer, and any
        // element-picker capture. AGENTS.md tells agents to parse it first.
        const envelope = reportEnvelope || buildEnvelope();
        // No explicit title: the backend auto-titles from the prompt intent.
        const out = await api('/dev/sessions', {
          prompt: text + envelopeBlock(envelope),
          images,
          files: docs,
          ...(pkg ? { package: pkg } : {}),
          mode,
          template: activeTemplate || '',
          ...modelPayload,
          agentOptions: agentOptionsPayload(agentOpts),
          ...(retryFamily ? { familyId: retryFamily } : {}),
        });
        setSelected(out.id);
        setTab('chat');
      }
      setPrompt('');
      setFiles([]);
      setRetryFamily(null);
      setReportEnvelope(null);
      setActiveTemplate(null);
      setFieldValues({});
    } catch (e) {
      uploadAbort.current = null;
      setUpload(null);
      setError(e?.name === 'AbortError' ? 'Upload cancelled — your files are still attached.' : e.message);
    } finally {
      setBusy(false);
    }
  };

  // Answers to a ```questions form go back as the next turn (same mode).
  const answerQuestions = async (ev, answers) => {
    if (!selected || busy) return;
    setBusy(true); setError(null);
    try {
      const lines = (ev.questions || []).map((q) => {
        const a = answers[q.id];
        const v = Array.isArray(a) ? a.join('; ') : String(a ?? '').trim();
        return `- ${q.question}\n  → ${v || '(no answer — use your recommendation)'}`;
      });
      await api(`/dev/sessions/${selected}/messages`, {
        prompt: `Answers to your questions:\n\n${lines.join('\n')}\n\nContinue with these decisions.`,
        answers: { questionsEventId: ev.id, ...answers },
        mode: session?.mode || 'agent',
      });
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // Plan approval flow: Approve & Execute sends the canned execute follow-up
  // in agent mode; Revise focuses the composer (still plan mode); Discard
  // archives the session.
  const approvePlan = async () => {
    if (!canApprovePlan) return;
    setBusy(true); setError(null);
    try {
      await api(`/dev/sessions/${selected}/messages`, {
        prompt: EXECUTE_PROMPT,
        mode: 'agent',
        ...(execModel ? { model: execModel, modelParams: buildParamsArray(execModelDef, execParams) } : {}),
      });
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  // The planning turn produced no plan card: ask for one explicitly (plan
  // mode stays on, nothing is edited).
  const requestPlan = async () => {
    if (!selected || busy) return;
    setBusy(true); setError(null);
    try {
      await api(`/dev/sessions/${selected}/messages`, {
        prompt: 'Write the complete implementation plan for my request now — every phase in full, with a one-line summary of how many phases there are. Do not change any files.',
        mode: 'plan',
      });
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const discardPlan = async () => {
    try {
      await api(`/dev/sessions/${selected}/archive`, {});
      setSelected(null);
    } catch (e) { setError(e.message); }
  };

  const toggleArchive = async () => {
    try {
      await api(`/dev/sessions/${selected}/${session?.archived ? 'unarchive' : 'archive'}`, {});
      if (!session?.archived) setSelected(null);
    } catch (e) { setError(e.message); }
  };

  const enableNotifications = async (on) => {
    if (!on) { saveSettings({ notify: false }); return; }
    if (typeof Notification === 'undefined') { setError('This browser does not support notifications.'); return; }
    const perm = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
    if (perm === 'granted') saveSettings({ notify: true });
    else setError('Notifications are blocked for this site — allow them in the browser settings.');
  };

  const stop = async () => {
    try { await api(`/dev/sessions/${selected}/cancel`, {}); } catch (e) { setError(e.message); }
  };

  // Retry: start a NEW session with this session's original prompt (failed
  // agents never started, so there is nothing to resume). The retry joins the
  // same familyId so attempts collapse into one sidebar thread.
  const retry = () => {
    const first = events.find((e) => e.type === 'user');
    // Strip a prior context envelope — send() attaches a fresh one.
    const raw = (first?.text || session?.title || '').split('\n---\ncontext.json')[0].trimEnd();
    setRetryFamily(session?.familyId || selected);
    setSelected(null);
    setPrompt(raw);
    setTab('chat');
  };

  const rename = async () => {
    const title = (editingTitle || '').trim();
    setEditingTitle(null);
    if (!title || title === session?.title) return;
    try { await api(`/dev/sessions/${selected}/rename`, { title }); } catch (e) { setError(e.message); }
  };

  const remove = async () => {
    setConfirmDelete(false);
    try {
      await api(`/dev/sessions/${selected}/delete`, {});
      setSelected(null);
    } catch (e) {
      setError(e.message);
    }
  };

  // Attach from any source (picker, paste, drop, folder pick). Any type;
  // whole folders keep their structure; vendored trees are skipped; limits
  // come from live config (dev.attachments) via /dev/status.
  const addEntries = (incoming, note = '') => {
    const { kept, skipped } = applySkips(incoming || [], setup?.attachments?.skip);
    setFiles((prev) => {
      const merged = [...prev];
      const seen = new Set(prev.map((e) => `${e.relPath}\u0000${e.file.size}`));
      for (const en of kept) {
        const sig = `${en.relPath}\u0000${en.file.size}`;
        if (!seen.has(sig)) { seen.add(sig); merged.push(en); }
      }
      const problem = checkLimits(merged, setup?.attachments);
      if (problem) { setError(problem); return prev; }
      return merged;
    });
    const notes = [note, skipped ? `${skipped} file${skipped > 1 ? 's' : ''} skipped (node_modules, .git, build output, empty files).` : ''].filter(Boolean);
    if (notes.length) setNotice(notes.join(' '));
  };
  const addFiles = (fileList) => addEntries(collectFromFileList(fileList));
  const removeEntry = (i) => setFiles((prev) => prev.filter((_, j) => j !== i));
  const removeFolder = (name) => setFiles((prev) => prev.filter((e) => e.relPath.split('/')[0] !== name || !e.relPath.includes('/')));

  // Paste anywhere in the composer (textarea, template fields, tray):
  // screenshots, copied files, or an image inside pasted HTML.
  const onPaste = async (e) => {
    const cd = e.clipboardData;
    const hasFile = Array.from(cd?.items || []).some((it) => it.kind === 'file') || cd?.files?.length > 0;
    const hasHtmlImg = !hasFile && /<img[^>]+src=/i.test(cd?.getData?.('text/html') || '');
    if (!hasFile && !hasHtmlImg) return; // plain text paste — let the field handle it
    e.preventDefault();
    const out = await collectFromPaste(e);
    if (out.entries.length) addEntries(out.entries, out.note);
    else if (out.text) { setPrompt((p) => `${p}${p ? '\n' : ''}${out.text}`); if (out.note) setNotice(out.note); }
    else if (out.note) setNotice(out.note);
  };

  // Folder picker: File System Access API where available, else the hidden
  // webkitdirectory input (set imperatively — React drops the attribute).
  const pickFolder = async () => {
    const out = await pickFolderEntries();
    if (out === null) { folderInput.current?.click(); return; }
    if (out.length) addEntries(out);
  };
  const onDragEnter = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };
  const onDrop = async (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    addEntries(await collectFromDataTransfer(e.dataTransfer));
  };

  const cost = costUsd(session?.usage);
  const elapsed = running && session?.createdAt ? Date.now() - (session.startedAt || session.createdAt) : session?.durationMs;

  // ---- render -------------------------------------------------------------------
  return (
    <div className="devc">
      <aside className="devc-side">
        <div className="devc-side-top">
          <button className="devc-btn primary devc-new" onClick={() => { setSelected(null); setPrompt(''); setView('sessions'); }}>
            + New session
          </button>
          <button
            className={`devc-btn devc-gear${settingsOpen ? ' active' : ''}`}
            title="Console settings: notifications and auto-archive"
            aria-label="Console settings"
            onClick={() => setSettingsOpen(!settingsOpen)}
          >
            ⚙
          </button>
        </div>
        {/* Operator views — session-independent surfaces (config / access / ops). */}
        <div className="devc-views">
          {[
            ['config', 'Config', 'Live settings the backend re-reads within a minute — no deploy'],
            ['access', 'Access', 'Who can sign in and what they can do (admin / product / tester / waitlist)'],
            ['ops', 'Ops', 'Health, logs and recent service events for this environment'],
            ['ecosystem', 'Ecosystem', 'Live picture of this app: services, monitor, security, twin, control plane'],
            ['agents', 'Agents', 'Eval runs and the policy denial trail for the embedded agents'],
          ].map(([v, label, tip]) => (
            <button
              key={v}
              className={`devc-filter${view === v ? ' active' : ''}`}
              title={tip}
              onClick={() => setView(view === v ? 'sessions' : v)}
            >
              {label}
            </button>
          ))}
        </div>
        {settingsOpen && (
          <div className="devc-settings">
            <label>
              <input
                type="checkbox"
                checked={settings.notify}
                onChange={(e) => enableNotifications(e.target.checked)}
              />
              Notify when a session finishes
            </label>
            <label>
              <input
                type="checkbox"
                checked={settings.autoArchive}
                onChange={(e) => saveSettings({ autoArchive: e.target.checked })}
              />
              Auto-archive failed sessions after 24h
            </label>
          </div>
        )}
        {setupFailed && !setup && (
          <div className="devc-warn">
            Can&apos;t reach the Dev Console API from this origin — status, the model list, and new
            agent runs will all fail. On a PR <em>preview</em> URL this means the live backend
            doesn&apos;t allow preview-channel origins yet: merge &amp; deploy the latest backend
            (<code>backend-fast</code>) from the production console, then Retry here.
          </div>
        )}
        {setup?.repo && (
          <a className="devc-repo-link" href={setup.repo} target="_blank" rel="noopener noreferrer" title="The layer repo agents work in — opens on your git provider">
            ⎇ Open layer repo <span className="devc-dim">{setup.repo.replace(/^https?:\/\//, '')}</span>
          </a>
        )}
        {setup?.key && (
          <div className={`devc-key ${setup.key.ok ? 'ok' : 'bad'}`} title={setup.key.ok ? setup.key.email : ''}>
            <span className="devc-key-dot" />
            {setup.key.ok
              ? <>Cursor key: <strong>{setup.key.name}</strong></>
              : <>Cursor API key rejected — rotate SSM <code>/&lt;project-slug&gt;/cursor-api-key</code> and restart the backend.</>}
          </div>
        )}
        {setup && !setup.ready && (
          <div className="devc-warn">
            {setup.runtime === 'runner'
              ? (!setup.repoConfigured
                ? <>No repo configured — set <code>DEV_REPO</code> for this instance to enable agent runs.</>
                : setup.driverKey === false
                  ? <>The <code>{setup.driver}</code> agent driver needs its key — store it at <code>{setup.driverKeyHint}</code> (or switch <code>dev.agentDriver</code> in live config).</>
                  : <>Git token missing for <code>{setup.gitProvider}</code> — the runner cannot clone or open PRs.</>)
              : <>Cursor API key missing. Store one at SSM <code>/&lt;project-slug&gt;/cursor-api-key</code> to enable agent runs.</>}
          </div>
        )}
        {setup?.ready && setup.repoConnected === false && (
          <div className="devc-warn">
            The repo isn&apos;t connected to Cursor cloud agents — runs will fail to start.
            Fix: <a href="https://cursor.com/dashboard/integrations" target="_blank" rel="noopener noreferrer">cursor.com/dashboard → Integrations</a> →
            grant the Cursor GitHub App access to <code>{setup.repo}</code>.
          </div>
        )}
        <input
          className="devc-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search sessions…"
        />
        <div className="devc-filters">
          {[['all', 'All'], ['live', 'Live'], ['finished', 'Done'], ['failed', 'Failed'], ['archived', 'Archived']].map(([k, label]) => (
            <button
              key={k}
              className={`devc-filter${statusFilter === k ? ' active' : ''}`}
              onClick={() => setStatusFilter(k)}
            >
              {label}
              {k === 'live' && sessions.some((s) => LIVE.includes(s.status)) && <span className="devc-pulse" />}
            </button>
          ))}
        </div>
        {visibleSessions.map((s) => {
          const c = costUsd(s.usage);
          return (
            <button
              key={s.id}
              className={`devc-sess${selected === s.id ? ' active' : ''}`}
              onClick={() => setSelected(s.id)}
            >
              <div className="devc-sess-title">
                {LIVE.includes(s.status) && <span className="devc-pulse" />}
                {s.title || s.id}
              </div>
              <div className="devc-sess-meta">
                <Chip status={s.status} />
                {s.mode === 'plan' && !s.planExecuted && <span className="devc-chip blue">Plan</span>}
                {s.attempts > 1 && <span className="devc-chip amber">×{s.attempts} tries</span>}
                {s.prUrl && <span className="devc-chip gray">PR</span>}
                {s.deployJobId && <span className="devc-chip gray">deploy</span>}
                <span className="devc-dim">{fmtAgo(s.createdAt)}{c != null ? ` · $${c.toFixed(2)}` : ''}</span>
              </div>
            </button>
          );
        })}
        {!visibleSessions.length && (
          <div className="devc-dim" style={{ padding: 10 }}>
            {sessions.length ? 'No sessions match.' : 'No sessions yet — describe a change below to start one.'}
          </div>
        )}
      </aside>

      <main
        className="devc-main"
        onPaste={onPaste}
        onDragEnter={onDragEnter}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {dragging && (
          <div className="devc-drop" aria-hidden="true">
            <div>Drop files or whole folders to attach</div>
          </div>
        )}

        {/* Operator views cover the session workspace (sidebar toggles back). */}
        {view !== 'sessions' && (
          <div className="devc-view-overlay">
            {view === 'config' && <ConfigPanel isAdmin={role === 'admin'} />}
            {view === 'access' && <AccessPanel isAdmin={role === 'admin'} />}
            {view === 'ops' && <OpsPanel />}
            {view === 'ecosystem' && <EcosystemPanel />}
            {view === 'agents' && <AgentsPanel />}
          </div>
        )}

        <div className="devc-head">
          <div className="devc-head-title">
            {session ? (
              editingTitle !== null ? (
                <input
                  className="devc-title-input"
                  value={editingTitle}
                  autoFocus
                  onChange={(e) => setEditingTitle(e.target.value)}
                  onBlur={rename}
                  onKeyDown={(e) => { if (e.key === 'Enter') rename(); if (e.key === 'Escape') setEditingTitle(null); }}
                />
              ) : (
                <h2 title="Click to rename" onClick={() => setEditingTitle(session.title || '')}>
                  {session.title || selected}
                  <span className="devc-edit-hint">✎</span>
                </h2>
              )
            ) : (
              <h2>Dev Console</h2>
            )}
            <div className="devc-dim">
              {session
                ? <>
                    {session.mode && session.mode !== 'agent' && <span className="devc-chip blue">{session.mode}</span>}
                    {session.model && (
                      <span className="devc-chip gray">
                        {session.model}
                        {session.modelParams?.length ? ` · ${session.modelParams.map((p) => p.value).join(' · ')}` : ''}
                      </span>
                    )}
                    {session.route?.tier && (
                      <span
                        className={`devc-chip ${session.route.tier === 'frontier' ? 'amber' : session.route.tier === 'bulk' ? 'green' : 'blue'}`}
                        title={`Smart routing (${session.route.engine}${session.route.jev && !session.route.jev.applied && session.route.jev.tier ? `, Jev said ${session.route.jev.tier}` : ''}): ${(session.route.reasons || []).join('; ')}`}
                      >
                        {session.route.escalated ? `${session.route.escalated.from} → ${session.route.tier}` : session.route.tier}
                      </span>
                    )}
                    {' '}{running ? <><strong className="devc-phase">{phaseLine(session)}</strong> · {fmtDur(elapsed)}</> : session.durationMs ? `took ${fmtDur(session.durationMs)}` : ''}
                    {!running && session.toolCalls ? <> · {session.toolCalls} tool call{session.toolCalls === 1 ? '' : 's'}</> : null}
                    {cost != null && <> · ${cost.toFixed(2)}</>}
                    {session.usage && <> · {fmtTok(session.usage.inputTokens)} in / {fmtTok(session.usage.outputTokens)} out</>}
                    {running && lastEventTs && <> · last activity {fmtAgo(lastEventTs)}</>}
                  </>
                : <>Cloud agent works on {setup?.repo || 'the repo'} and opens a PR — nothing deploys until you click Merge &amp; Deploy.</>}
            </div>
          </div>
          <div className="devc-head-actions">
            {session && <Chip status={session.status} />}
            {session?.mode === 'plan' && !session.planExecuted && <span className="devc-chip blue" title={session.modeAuto ? `Auto: ${session.modeReason}` : 'Plan first'}>Plan{session.modeAuto ? ' · auto' : ''}</span>}
            {session?.awaitingAnswers && <span className="devc-chip gray">Waiting for your answers</span>}
            {session?.archived && <span className="devc-chip gray">Archived</span>}
            {session?.agentId && (
              <a
                className="devc-btn"
                href={`https://cursor.com/agents?id=${session.agentId}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Inspect or continue this agent in Cursor's own UI"
              >
                Open in Cursor ↗
              </a>
            )}
            {running && <button className="devc-btn danger" onClick={stop}>■ Stop</button>}
            {session && !running && <button className="devc-btn" title="Start a new session with this prompt" onClick={retry}>↻ Retry</button>}
            {session && !running && (
              <button className="devc-btn" title={session.archived ? 'Restore to the main list' : 'Move out of the main list (reversible)'} onClick={toggleArchive}>
                {session.archived ? '⬆ Unarchive' : '📥 Archive'}
              </button>
            )}
            {session && !running && (
              confirmDelete
                ? <>
                    <button className="devc-btn danger" onClick={remove}>Confirm delete</button>
                    <button className="devc-btn" onClick={() => setConfirmDelete(false)}>Keep</button>
                  </>
                : <button className="devc-btn" onClick={() => setConfirmDelete(true)}>🗑</button>
            )}
          </div>
        </div>

        {selected && (
          <div className="devc-tabs">
            {TABS.map(([k, label]) => (
              <button key={k} className={`devc-tab${tab === k ? ' active' : ''}`} onClick={() => setTab(k)}>
                {label}
                {k === 'pr' && session?.prUrl && <span className="devc-tab-dot green" />}
                {k === 'preview' && session?.previewUrl && <span className="devc-tab-dot green" />}
                {k === 'terminal' && running && <span className="devc-tab-dot blue" />}
              </button>
            ))}
            <label className="devc-dim devc-tab-opt">
              <input type="checkbox" checked={showThinking} onChange={(e) => setShowThinking(e.target.checked)} /> thinking
            </label>
            <label className="devc-dim devc-tab-opt">
              <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} /> auto-scroll
            </label>
          </div>
        )}

        {selected && familyAttempts.length > 1 && (
          <div className="devc-attempts">
            <span className="devc-dim">Attempts:</span>
            {familyAttempts.map((a, i) => (
              <button
                key={a.id}
                className={`devc-filter${a.id === selected ? ' active' : ''}`}
                title={`${CHIP[a.status]?.[0] || a.status} · ${fmtAgo(a.createdAt)}`}
                onClick={() => setSelected(a.id)}
              >
                #{i + 1} {FAILED.includes(a.status) ? '✗' : a.status === 'finished' ? '✓' : '…'}
              </button>
            ))}
          </div>
        )}

        <div className="devc-panes">
          {(!selected || tab === 'chat') && (
            <section className={`devc-chat${!selected ? ' devc-chat-hero' : ''}`}>
              {!selected && (
                <div className="devc-hero">
                  <h3>Start a dev cycle</h3>
                  <p>
                    Describe the change you want — bug fix, new feature, copy tweak, or “build an app that…”.
                    Paste screenshots, drop files or whole folders (specs, data, exports, code — any type, thousands of
                    files are fine); the agent gets them with the prompt. Big or vague asks get a plan you approve first;
                    small ones go straight to a pull request you review and ship from here.
                  </p>
                  <div className="devc-tpl-grid">
                    {TEMPLATES.map((t) => (
                      <button
                        key={t.id}
                        className={`devc-tpl-card${activeTemplate === t.id ? ' active' : ''}`}
                        onClick={() => {
                          setActiveTemplate(activeTemplate === t.id ? null : t.id);
                          setFieldValues({});
                        }}
                      >
                        <span className="devc-tpl-icon" aria-hidden>{t.icon}</span>
                        <span className="devc-tpl-label">{t.label}</span>
                        <span className="devc-tpl-desc">{t.desc}</span>
                      </button>
                    ))}
                  </div>
                  <div className="devc-hero-hint devc-dim">
                    Pick a starting point above, or just type below. <strong>Auto</strong> plans first when the ask is
                    feature-sized and asks you questions when a decision is yours; force <strong>Plan</strong> or
                    <strong>Agent</strong> any time.
                  </div>
                  {view === 'sessions' && <EcosystemWidget onOpen={() => setView('ecosystem')} />}
                </div>
              )}
              {groupedEvents.map((e) => {
                if (e.type === 'toolgroup') return <ToolGroup key={e.id} items={e.items} />;
                if (e.type === 'status') {
                  const at = new Date(e.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                  return (
                    <div key={e.id} className={`devc-step${e.status === 'warn' ? ' is-warn' : ''}`} role="status">
                      <span className="devc-step-dot" aria-hidden="true" />
                      <span className="devc-step-text">{e.message}</span>
                      <span className="devc-step-at">{at}</span>
                    </div>
                  );
                }
                if (e.type === 'user') {
                  // The context envelope travels with the prompt but renders
                  // as a compact chip, not a wall of JSON.
                  const [intent] = (e.text || '').split('\n---\ncontext.json');
                  const hasEnvelope = intent.length !== (e.text || '').length;
                  return (
                    <div key={e.id} className="devc-msg user">
                      <div className="devc-msg-who">
                        You{e.images ? ` · ${e.images} image${e.images > 1 ? 's' : ''}` : ''}
                        {(e.attachments || []).filter((a) => a.kind !== 'image').length > 0 && ` · ${(e.attachments || []).filter((a) => a.kind !== 'image').length} file${(e.attachments || []).filter((a) => a.kind !== 'image').length > 1 ? 's' : ''}`}
                        {e.package && <span className="devc-chip gray" style={{ marginLeft: 8 }} title={e.package.name}>🗂 {e.package.name} · {Number(e.package.files).toLocaleString()} files</span>}
                        {e.answers && <span className="devc-chip blue" style={{ marginLeft: 8 }}>answers</span>}
                        {hasEnvelope && <span className="devc-chip gray" style={{ marginLeft: 8 }}>context.json attached</span>}
                      </div>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{intent.trimEnd()}</ReactMarkdown>
                    </div>
                  );
                }
                if (e.type === 'assistant') {
                  return (
                    <div key={e.id} className="devc-msg agent">
                      <div className="devc-msg-who">Agent</div>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{e.text}</ReactMarkdown>
                    </div>
                  );
                }
                if (e.type === 'thinking' && showThinking) {
                  return <div key={e.id} className="devc-msg thinking">{e.text}</div>;
                }
                if (e.type === 'questions') {
                  const answered = events.some((u) => u.type === 'user' && u.seq > e.seq);
                  return <QuestionsCard key={e.id} e={e} answered={answered} busy={busy || running} onAnswer={(answers) => answerQuestions(e, answers)} />;
                }
                if (e.type === 'plan') {
                  return (
                    <PlanCard
                      key={e.id}
                      e={e}
                      superseded={e.id !== latestPlan?.id}
                      pending={e.id === latestPlan?.id && planPending}
                      execLabel={execTargetId}
                      execInvalid={execTargetInvalid}
                    />
                  );
                }
                if (e.type === 'result') {
                  // The run's final text usually repeats the assistant bubbles
                  // the member just read — show the status strip only then.
                  const repeated = resultRepeatsAssistant(e, events);
                  return (
                    <div key={e.id} className={`devc-msg result ${e.status}${repeated || !e.text ? ' slim' : ''}`}>
                      <div className="devc-msg-who">Run {e.status}{repeated && e.prUrl ? ' · pull request opened' : ''}</div>
                      {e.error && <div className="devc-err">{e.error}</div>}
                      {e.text && !repeated && <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{e.text}</ReactMarkdown>}
                      {e.prUrl && <a className="devc-link" href={e.prUrl} target="_blank" rel="noopener noreferrer">{e.prUrl}</a>}
                      {e.status === 'finished' && e.prUrl && (
                        <div className="devc-result-actions">
                          {session?.previewUrl
                            ? <button className="devc-btn" onClick={() => setTab('preview')}>Open preview ↗</button>
                            : <button className="devc-btn" onClick={() => setTab('pr')}>Build preview</button>}
                          <button className="devc-btn primary" onClick={() => setTab('pr')}>Review diff &amp; deploy →</button>
                        </div>
                      )}
                      {!running && e.id === lastResultId && <SessionFeedback sessionId={selected} feedback={session?.feedback} />}
                    </div>
                  );
                }
                return null;
              })}
              {running && (
                <div className={`devc-working${stalled ? ' is-stalled' : ''}`} role="status">
                  <span className={`devc-pulse${stalled ? ' is-stalled' : ''}`} />
                  {session?.status === 'starting' && !session?.phase
                    ? <>Waiting for the runner to start{elapsed ? ` · ${fmtDur(elapsed)}` : ''}…</>
                    : <>{phaseLine(session)}{liveTs ? ` — last activity ${fmtAgo(liveTs)}` : ''}…</>}
                  {liveThought && <span className="devc-working-thought">{liveThought}</span>}
                  {stalled && (
                    <span className="devc-stall-note">
                      No activity for {fmtDur(Date.now() - liveTs)}. A long tool call can look like this; if the runner died it is marked failed automatically within a few minutes — or Stop and Retry now.
                    </span>
                  )}
                </div>
              )}
              {session && FAILED.includes(session.status) && session.errorMessage && (
                <div className="devc-err" style={{ margin: '8px 0' }}>
                  {session.errorMessage}
                  {failureHint(session) && <div className="devc-hint">{failureHint(session)}</div>}
                  {session.requestId && (
                    <div className="devc-reqid">
                      request id <code>{session.requestId}</code>
                      <button
                        className="devc-filter"
                        title="Copy — Cursor support asks for this id"
                        onClick={() => navigator.clipboard?.writeText(session.requestId).catch(() => {})}
                      >
                        copy
                      </button>
                    </div>
                  )}
                  <div><button className="devc-btn" style={{ marginTop: 6 }} onClick={retry}>↻ Retry with same prompt</button></div>
                </div>
              )}
              {planPending && session.status === 'finished' && (
                <div className="devc-approve">
                  {latestPlan?.text ? (
                    <div className="devc-approve-label">
                      Review the plan above — the agent hasn&apos;t touched any files. Execute it, refine it, or drop it.
                    </div>
                  ) : (
                    // Approval is gated on a rendered plan: no plan card in
                    // chat (empty, failed, or a pre-plan-event session) means
                    // nothing reviewable will execute — regenerate first.
                    <div className="devc-approve-label devc-approve-warn">
                      The agent finished without writing a plan. Ask it to write the complete plan now, or switch
                      to Agent mode and describe what to build directly.
                      <div><button className="devc-btn" style={{ marginTop: 8 }} disabled={busy} onClick={requestPlan}>Ask for the full plan</button></div>
                    </div>
                  )}
                  {setup?.models?.length > 0 && (
                    <div className="devc-approve-config">
                      <span className="devc-param">
                        <span>Execute with</span>
                        <ModelSelector
                          models={setup.models}
                          value={execModel}
                          onChange={pickExecModel}
                          emptyLabel={`${session?.model || 'plan model'} (keep)`}
                          excludeIds={['default']}
                        />
                      </span>
                      <ModelParamsSelector modelDef={execModelDef} picks={execParams} onChange={setExecParams} />
                      <span className="devc-dim">
                        Planning is done — a cheaper model can often execute a good plan.
                      </span>
                    </div>
                  )}
                  {execTargetInvalid && (
                    <div className="devc-err" style={{ marginBottom: 10 }}>
                      Model &lsquo;{execTargetRaw}&rsquo; isn&apos;t in the current model catalog, so executing
                      would fail at run start. Pick a model above, or Retry the model list from the composer.
                    </div>
                  )}
                  <div className="devc-approve-actions">
                    <button
                      className="devc-btn primary"
                      disabled={busy || !canApprovePlan}
                      title={!latestPlan?.text
                        ? 'A plan must render in chat before it can be approved'
                        : execTargetInvalid ? 'The selected execution model is not in the catalog' : ''}
                      onClick={approvePlan}
                    >
                      ✓ Approve &amp; Execute
                    </button>
                    <button className="devc-btn" onClick={() => composerInput.current?.focus()}>Revise plan…</button>
                    <button className="devc-btn danger" onClick={discardPlan}>Discard</button>
                  </div>
                </div>
              )}
              {selected && session?.prUrl && tab === 'chat' && (
                <button className="devc-btn" style={{ alignSelf: 'flex-start' }} onClick={() => setTab('pr')}>
                  View PR &amp; Deploy →
                </button>
              )}
              <div ref={chatEnd} />
            </section>
          )}

          {selected && tab === 'activity' && (
            <section className="devc-chat devc-activity">
              {events.filter((e) => e.type !== 'thinking' || showThinking).map((e) => <ActivityRow key={e.id} e={e} />)}
              {!events.length && <div className="devc-dim">Every event — messages, tool calls, tokens, results — lands here in real time.</div>}
              <div ref={chatEnd} />
            </section>
          )}

          {selected && tab === 'terminal' && (
            <section className="devc-term devc-term-full">
              <div className="devc-term-head">
                Terminal — agent shell + deploy output
                <span className="devc-term-tools">
                  {[['all', 'All'], ['agent', 'Agent'], ['deploy', 'Deploy']].map(([k, label]) => (
                    <button key={k} className={`devc-filter${termFilter === k ? ' active' : ''}`} onClick={() => setTermFilter(k)}>{label}</button>
                  ))}
                  <button
                    className="devc-filter"
                    onClick={() => navigator.clipboard?.writeText(terminal.map((r) => r.text).join('\n')).catch(() => {})}
                  >
                    Copy
                  </button>
                  <button
                    className="devc-filter"
                    title="Download the complete session record — every event plus deploy logs"
                    onClick={() => {
                      const lines = events.map((e) => {
                        const at = e.ts ? new Date(e.ts).toISOString() : '';
                        if (e.type === 'tool') return `[${at}] TOOL ${e.name} (${e.status})\n${e.args || ''}\n${e.result || ''}`;
                        if (e.type === 'result') return `[${at}] RESULT ${e.status} ${e.error || ''}\n${e.text || ''}`;
                        return `[${at}] ${String(e.type).toUpperCase()}\n${e.text || e.message || ''}`;
                      });
                      jobLogs.forEach((l) => lines.push(`[${new Date(l.ts).toISOString()}] DEPLOY\n${l.text}`));
                      const blob = new Blob([lines.join('\n\n')], { type: 'text/plain' });
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = `dev-session-${selected}.txt`;
                      a.click();
                      URL.revokeObjectURL(a.href);
                    }}
                  >
                    Download
                  </button>
                </span>
              </div>
              <div className="devc-term-body">
                {terminal.map((r, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <pre key={i} className={r.deploy ? 'deploy' : ''}>{r.text}</pre>
                ))}
                {!terminal.length && <div className="devc-dim">Shell commands and deploy logs stream here.</div>}
                <div ref={termEnd} />
              </div>
            </section>
          )}

          {selected && tab === 'pr' && (
            <section className="devc-chat">
              <PreviewCard session={session} sessionId={selected} onReady={() => setTab('preview')} />
              <DiffViewer sessionId={selected} prUrl={session?.prUrl} />
              <DeployCard session={session} sessionId={selected} setup={setup} />
            </section>
          )}

          {selected && tab === 'preview' && (
            <section className="devc-preview">
              {session?.previewUrl ? (
                <>
                  <div className="devc-preview-bar">
                    <span className="devc-dim">Preview of PR branch — production is untouched.</span>
                    <a className="devc-link" href={session.previewUrl} target="_blank" rel="noopener noreferrer">{session.previewUrl}</a>
                    <span className="devc-preview-tools">
                      <button className="devc-filter" onClick={() => window.open(session.previewUrl, '_blank', 'noopener')}>Open in new tab ↗</button>
                      <button className="devc-filter" onClick={() => setTab('pr')}>Rebuild / Deploy →</button>
                    </span>
                  </div>
                  <iframe className="devc-preview-frame" src={session.previewUrl} title="UI preview" />
                  <div className="devc-dim" style={{ padding: '6px 2px' }}>
                    The preview runs on its own temporary domain, so sign in once inside the frame (the domain is
                    auto-authorized). Expires after 7 days.
                  </div>
                </>
              ) : (
                <div className="devc-empty" style={{ padding: 16 }}>
                  <h3>No preview yet</h3>
                  <p>
                    Once the agent opens a PR, build a preview from the <strong>PR &amp; Deploy</strong> tab — you get a
                    temporary URL of the app with the change applied, before anything is merged or deployed.
                  </p>
                  <PreviewCard session={session} sessionId={selected} />
                </div>
              )}
            </section>
          )}

          {selected && tab === 'artifacts' && (
            <section className="devc-chat">
              <ArtifactsPanel sessionId={selected} running={!!running} />
            </section>
          )}

          {selected && tab === 'usage' && (
            <section className="devc-chat">
              <UsagePanel sessionId={selected} />
            </section>
          )}

          {selected && tab === 'details' && session && (
            <section className="devc-chat">
              <div className="devc-card">
                <div className="devc-card-title">Session details</div>
                <div className="devc-details">
                  <div><span>Status</span><Chip status={session.status} /></div>
                  <div>
                    <span>Mode</span>
                    {session.mode === 'plan'
                      ? (session.planExecuted ? 'plan → executed' : 'plan (awaiting approval)')
                      : (session.mode || '—')}
                  </div>
                  <div><span>Model</span>{session.model || '—'}</div>
                  <div>
                    <span>Model params</span>
                    {session.modelParams?.length
                      ? session.modelParams.map((p) => `${p.id}=${p.value}`).join(', ')
                      : '—'}
                  </div>
                  <div>
                    <span>Agent options</span>
                    {session.agentOptions
                      ? [
                          session.agentOptions.autoCreatePR === false && 'no auto-PR',
                          session.agentOptions.skipReviewerRequest === false && 'request reviewer',
                          session.agentOptions.workOnCurrentBranch && 'current branch',
                          session.agentOptions.openAsCursorGithubApp === true && 'PR as Cursor app',
                          session.agentOptions.openAsCursorGithubApp === false && 'PR as key owner',
                          session.agentOptions.startingRef && `from ${session.agentOptions.startingRef}`,
                        ].filter(Boolean).join(', ') || 'defaults'
                      : '—'}
                  </div>
                  <div><span>Created</span>{session.createdAt ? new Date(session.createdAt).toLocaleString() : '—'}</div>
                  <div><span>By</span>{session.createdByEmail || session.createdBy || '—'}</div>
                  <div><span>Duration</span>{fmtDur(session.durationMs)}</div>
                  <div><span>Tokens in</span>{fmtTok(session.usage?.inputTokens)}</div>
                  <div><span>Tokens out</span>{fmtTok(session.usage?.outputTokens)}</div>
                  <div><span>Cache read</span>{fmtTok(session.usage?.cacheReadTokens)}</div>
                  <div><span>Cost</span>{cost != null ? `$${cost.toFixed(3)}` : session.usage ? 'pending' : '—'}</div>
                  <div><span>Runs</span>{session.usage?.runs ?? '—'}</div>
                  <div>
                    <span>Agent</span>
                    {session.agentId
                      ? <a className="devc-link" href={`https://cursor.com/agents/${session.agentId}`} target="_blank" rel="noopener noreferrer">{session.agentId}</a>
                      : '—'}
                  </div>
                  <div><span>PR</span>{session.prUrl ? <a className="devc-link" href={session.prUrl} target="_blank" rel="noopener noreferrer">{session.prUrl}</a> : '—'}</div>
                  <div><span>Branch</span>{session.branch || '—'}</div>
                  <div><span>Deploy job</span>{session.deployJobId || '—'}</div>
                </div>
                <div className="devc-dim" style={{ marginTop: 8 }}>
                  Cost reconciles hourly (Cursor billing is eventually consistent). Session costs roll up on the
                  Cost Report page.
                </div>
              </div>
            </section>
          )}
        </div>

        <div className="devc-compose">
          {selected && (
            <div className="devc-compose-ctx">
              <span className="devc-dim">
                {session?.mode === 'plan' && !session?.planExecuted ? 'Refining plan:' : 'Follow-up to:'}
              </span>
              <strong>{session?.title || selected}</strong>
              <button
                className="devc-filter"
                title="Switch to a new session"
                onClick={() => { setSelected(null); setPrompt(''); }}
              >
                ✕ new session
              </button>
            </div>
          )}
          {!selected && activeTemplate && (
            <div className="devc-fields">
              {(TEMPLATES.find((t) => t.id === activeTemplate)?.fields || []).map((f) => (
                <label key={f.key} className="devc-field">
                  <span>{f.label}</span>
                  <input
                    value={fieldValues[f.key] || ''}
                    placeholder={f.ph}
                    onChange={(e) => setFieldValues({ ...fieldValues, [f.key]: e.target.value })}
                  />
                </label>
              ))}
              <button
                className="devc-filter"
                title="Clear template"
                onClick={() => { setActiveTemplate(null); setFieldValues({}); }}
              >
                ✕ {TEMPLATES.find((t) => t.id === activeTemplate)?.label}
              </button>
            </div>
          )}
          <AttachmentTray
            entries={files}
            upload={upload}
            onRemove={removeEntry}
            onRemoveFolder={removeFolder}
            onClear={() => setFiles([])}
          />
          {notice && (
            <div className="devc-notice" role="status">
              {notice}
              <button type="button" className="devc-filter" onClick={() => setNotice('')}>Dismiss</button>
            </div>
          )}
          {error && <div className="devc-err">{error}</div>}
          <div className="devc-config">
            <ModeSelector modes={setup?.modes} value={mode} onChange={pickMode} />
            {(setup?.models?.length > 0) ? (
              <ModelSelector
                models={setup.models}
                value={model}
                onChange={pickModel}
                // '' keeps the conversation's current model on follow-ups;
                // on new sessions the backend routes each ask to a tier
                // (bulk / strong / frontier) and picks the model for it.
                emptyLabel={selected ? `${session?.model || 'current model'} (keep)` : `Auto — smart routing${setup.router?.engine && setup.router.engine !== 'rules' ? ` (${setup.router.engine})` : ''}`}
                excludeIds={['default']}
              />
            ) : (
              // Catalog unavailable: either /dev/status itself is unreachable
              // (setupFailed — nothing on this page will work) or the status
              // loaded but Cursor's model list failed (runs still work on the
              // server-side default model). Retry re-fetches with cache bypass.
              <span
                className="devc-param devc-model-fallback"
                title={
                  setupFailed
                    ? 'Dev Console API unreachable from this origin — see the warning in the sidebar'
                    : setup?.modelsError || 'Model catalog unavailable — runs use the default model'
                }
              >
                <span>Model</span>
                <select disabled>
                  <option>
                    {setup ? `${setup.model} (default)` : setupFailed ? 'API unreachable' : 'loading…'}
                  </option>
                </select>
                {(setup || setupFailed) && (
                  <button type="button" className="devc-filter" onClick={refreshStatus} disabled={statusBusy} title="Re-fetch the model list from Cursor (runs still work with the default model meanwhile)">
                    {statusBusy ? 'Retrying…' : 'Retry model list'}
                  </button>
                )}
              </span>
            )}
            <ModelParamsSelector modelDef={modelDef} picks={modelParams} onChange={pickParams} />
            {setup?.modelsError && !(setup?.models?.length > 0) && (
              <span className="devc-dim" title={setup.modelsError}>model list failed — using default</span>
            )}
            {!selected && <AdvancedAgentOptions value={agentOpts} onChange={setAgentOpts} />}
          </div>
          <div className="devc-compose-row">
            <button
              className="devc-btn"
              title={`Attach files — screenshots, documents (${DOC_EXT_LIST}), data, code… any type`}
              onClick={() => fileInput.current?.click()}
            >
              📎
            </button>
            <button
              className="devc-btn"
              title="Attach a whole folder (structure preserved — hundreds or thousands of files are fine)"
              onClick={pickFolder}
            >
              📁
            </button>
            <input
              ref={fileInput}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            />
            <input
              ref={(el) => { folderInput.current = el; if (el) { el.setAttribute('webkitdirectory', ''); el.setAttribute('directory', ''); } }}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            />
            <textarea
              ref={composerInput}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || e.nativeEvent?.isComposing) return;
                if (e.shiftKey || e.altKey) return; // new line
                e.preventDefault();
                send();
              }}
              placeholder={
                selected
                  ? (session?.mode === 'plan' && !session?.planExecuted
                    ? 'Refine the plan… (↵ to send, ⇧↵ for a new line)'
                    : 'Follow up with the agent… paste screenshots, drop files or folders (↵ to send, ⇧↵ for a new line)')
                  : activeTemplate
                    ? 'Anything else to add? (optional — fields above carry the ask)'
                    : 'Describe what to build… paste screenshots, drop files or whole folders (↵ to send, ⇧↵ for a new line)'
              }
              rows={3}
            />
            <div className="devc-compose-side">
              <button className="devc-btn primary" disabled={busy || !canSend || (running && !!selected)} onClick={send}>
                {busy ? (upload ? 'Uploading…' : 'Sending…') : selected ? 'Send' : mode === 'plan' ? 'Start plan' : mode === 'auto' ? 'Start' : 'Start session'}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
